export type ApiParamType =
  | "string"
  | "number"
  | "boolean"
  | "string[]"
  | "number[]"
  | "object"
  | "object[]";

export interface ApiParam {
  key: string;
  type: ApiParamType;
  required?: boolean;
  description?: string;
}

export interface ApiResponseField {
  key: string;
  type: ApiParamType;
  description?: string;
}

export type ApiResponseType = "json" | "blob" | "text";

export interface ApiEndpoint {
  name: string;
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  version: string;
  endpoint: string;
  params?: ApiParam[];
  response?: ApiResponseField[];
  responseType?: ApiResponseType;
}

export interface ApiCategory {
  id: string;
  label: string;
  section: "user" | "admin";
  apis: ApiEndpoint[];
}

export const API_DATA: ApiCategory[] = [
  // ──────────────── 사용자 섹션 ────────────────
  {
    id: "doc",
    label: "문서",
    section: "user",
    apis: [
      {
        name: "getContentDetail",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/contents/detail/{workgroupId}/{syncId}",
        response: [
          { key: "data", type: "object", description: "문서 상세 정보 객체" },
          { key: "data.syncId", type: "string", description: "문서 고유 ID" },
          { key: "data.contentName", type: "string", description: "문서 이름" },
          { key: "data.revisionNo", type: "number", description: "최신 리비전 번호" },
          { key: "data.fileSize", type: "number", description: "파일 크기 (bytes)" },
          { key: "data.fileType", type: "string", description: "파일 확장자" },
          { key: "data.workgroupId", type: "string", description: "워크그룹 ID" },
          { key: "data.createdDate", type: "string", description: "생성일시" },
          { key: "data.modifiedDate", type: "string", description: "수정일시" },
          { key: "data.checkedOut", type: "boolean", description: "체크아웃 여부" },
          { key: "data.favorite", type: "boolean", description: "즐겨찾기 여부" },
        ],
        responseType: "json",
      },
      {
        name: "getContentSimpleDetail",
        method: "GET",
        version: "3.0",
        endpoint: "/papi/file/detail/{syncId}",
        response: [
          { key: "data", type: "object", description: "문서 간략 정보 객체" },
          { key: "data.syncId", type: "string", description: "문서 고유 ID" },
          { key: "data.contentName", type: "string", description: "문서 이름" },
          { key: "data.fileType", type: "string", description: "파일 확장자" },
          { key: "data.fileSize", type: "number", description: "파일 크기 (bytes)" },
          { key: "data.workgroupId", type: "string", description: "워크그룹 ID" },
        ],
        responseType: "json",
      },
      {
        name: "getContentList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/list",
        params: [
          {
            key: "workgroupId",
            type: "string",
            required: true,
            description: "워크그룹 ID",
          },
          {
            key: "page",
            type: "number",
            description: "페이지 번호 (기본값: 0)",
          },
          { key: "pageSize", type: "number", description: "페이지당 항목 수" },
          { key: "searchOpt", type: "string", description: "검색어" },
          { key: "folderId", type: "string", description: "폴더 ID" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "콘텐츠 목록" },
          { key: "data.content[].syncId", type: "string", description: "문서 고유 ID" },
          { key: "data.content[].contentName", type: "string", description: "문서 이름" },
          { key: "data.content[].fileSize", type: "number", description: "파일 크기 (bytes)" },
          { key: "data.content[].createdDate", type: "string", description: "생성일시" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "getContentFavorite",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/favorite",
        params: [
          {
            key: "flag",
            type: "boolean",
            required: true,
            description: "즐겨찾기 설정 여부 (true: 추가, false: 해제)",
          },
          {
            key: "syncIds",
            type: "string[]",
            required: true,
            description: "대상 문서 ID 목록",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getContentThumbnail",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/contents/thumbnail/{syncId}/{revisionNo}",
        responseType: "blob",
      },
      {
        name: "getReadRequestContentList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/request/read/unread",
        params: [
          {
            key: "workgroupId",
            type: "string",
            required: true,
            description: "워크그룹 ID",
          },
        ],
        response: [
          { key: "data", type: "object[]", description: "미읽은 열람 요청 문서 목록" },
          { key: "data[].syncId", type: "string", description: "문서 고유 ID" },
          { key: "data[].contentName", type: "string", description: "문서 이름" },
          { key: "data[].requestDate", type: "string", description: "요청일시" },
          { key: "data[].requesterId", type: "string", description: "요청자 ID" },
        ],
        responseType: "json",
      },
      {
        name: "getDocUsingByWebIncludeToken",
        method: "GET",
        version: "~2.9",
        endpoint:
          "/wapi/contents/webusing/{permission}/{workgroupId}/{syncId}/{revisionNo}",
        response: [
          { key: "data", type: "object", description: "웹 뷰어 접근 정보" },
          { key: "data.viewUrl", type: "string", description: "웹 뷰어 URL" },
          { key: "data.token", type: "string", description: "뷰어 접근 토큰" },
        ],
        responseType: "json",
      },
      {
        name: "downloadDoc (single)",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/contents/download/{syncId}/revisions/0",
        responseType: "blob",
      },
      {
        name: "downloadDoc (multi)",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/download/",
        params: [
          {
            key: "syncIds",
            type: "string[]",
            required: true,
            description: "다운로드할 문서 ID 목록",
          },
        ],
        responseType: "blob",
      },
      {
        name: "downloadDocCheck",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/downloadCheck",
        params: [
          {
            key: "syncIds",
            type: "string[]",
            required: true,
            description: "다운로드 가능 여부를 확인할 문서 ID 목록",
          },
        ],
        response: [
          { key: "data", type: "object", description: "syncId별 다운로드 가능 여부 맵 (syncId → boolean)" },
        ],
        responseType: "json",
      },
      {
        name: "downloadDoc (by revision)",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/contents/download/{syncId}/revisions/{revisionNo}",
        responseType: "blob",
      },
      {
        name: "downloadDoc (decrypt)",
        method: "GET",
        version: "~2.9",
        endpoint:
          "/wapi/contents/download/{syncId}/revisions/{revisionNo}/decrypt",
        responseType: "blob",
      },
      {
        name: "deleteDoc",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/delete",
        params: [
          {
            key: "syncIds",
            type: "string[]",
            required: true,
            description: "휴지통으로 이동할 문서 ID 목록",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "deleteDocNow",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/delete/now",
        params: [
          {
            key: "syncIds",
            type: "string[]",
            required: true,
            description: "즉시 삭제할 문서 ID 목록",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "restoreDoc",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/restore",
        params: [
          {
            key: "syncIds",
            type: "string[]",
            required: true,
            description: "휴지통에서 복원할 문서 ID 목록",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "createUploadUid",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/upload/uid/create",
        response: [
          { key: "data", type: "object", description: "업로드 세션 정보" },
          { key: "data.uid", type: "string", description: "업로드 고유 UID" },
        ],
        responseType: "json",
      },
      {
        name: "uploadDoc",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/upload/{uid}",
        response: [
          { key: "data", type: "object", description: "업로드된 파일 정보" },
          { key: "data.fileId", type: "string", description: "업로드된 파일 ID" },
          { key: "data.fileName", type: "string", description: "파일 이름" },
          { key: "data.fileSize", type: "number", description: "파일 크기 (bytes)" },
        ],
        responseType: "json",
      },
      {
        name: "removeUploadFile",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/upload/{uid}/delete/{fileId}",
        response: [
        ],
        responseType: "json",
      },
      {
        name: "changeDocName",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/docname",
        params: [
          {
            key: "syncId",
            type: "string",
            required: true,
            description: "이름을 변경할 문서 ID",
          },
          {
            key: "contentName",
            type: "string",
            required: true,
            description: "변경할 문서 이름",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "setMasterDoc",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/master/changes",
        params: [
          { key: "syncIds", type: "string[]", required: true, description: "마스터 문서로 설정할 문서 ID 목록" },
          { key: "master", type: "object", required: true, description: "마스터 변경 정보 객체" },
          { key: "master.fromUserId", type: "string", required: true, description: "현재 마스터 소유자 ID" },
          { key: "master.toUserId", type: "string", required: true, description: "새 마스터 소유자 ID" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getMemos",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/logs/doc/{syncId}",
        response: [
          { key: "data", type: "object[]", description: "메모(로그) 목록" },
          { key: "data[].logId", type: "string", description: "로그 ID" },
          { key: "data[].content", type: "string", description: "메모 내용" },
          { key: "data[].createdDate", type: "string", description: "작성일시" },
          { key: "data[].userId", type: "string", description: "작성자 ID" },
        ],
        responseType: "json",
      },
      {
        name: "requestRead",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/request/read",
        params: [
          {
            key: "syncId",
            type: "string",
            required: true,
            description: "열람 요청할 문서 ID",
          },
          {
            key: "workgroupId",
            type: "string",
            required: true,
            description: "워크그룹 ID",
          },
          {
            key: "userIds",
            type: "string[]",
            required: true,
            description: "열람 요청 대상 사용자 ID 목록",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getReadRequests",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/contents/request/read/{syncId}",
        response: [
          { key: "data", type: "object[]", description: "열람 요청 목록" },
          { key: "data[].requestId", type: "string", description: "요청 ID" },
          { key: "data[].userId", type: "string", description: "요청 대상 사용자 ID" },
          { key: "data[].status", type: "string", description: "요청 상태" },
          { key: "data[].requestDate", type: "string", description: "요청일시" },
        ],
        responseType: "json",
      },
      {
        name: "deleteReadRequest",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/request/read/{syncId}/{requestId}/delete",
        response: [
        ],
        responseType: "json",
      },
      {
        name: "checkoutDoc",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/checkout/{workgroupId}/{syncId}/{revisionNo}",
        response: [
          { key: "data", type: "object", description: "체크아웃 정보" },
          { key: "data.syncId", type: "string", description: "문서 고유 ID" },
          { key: "data.checkedOutBy", type: "string", description: "체크아웃 사용자 ID" },
          { key: "data.checkedOutDate", type: "string", description: "체크아웃 일시" },
        ],
        responseType: "json",
      },
      {
        name: "undoCheckoutDoc",
        method: "POST",
        version: "~2.9",
        endpoint:
          "/wapi/contents/undocheckout/{workgroupId}/{syncId}/{revisionNo}",
        response: [
        ],
        responseType: "json",
      },
      {
        name: "createFolder",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/folder/create",
        params: [
          {
            key: "name",
            type: "string",
            required: true,
            description: "폴더 이름",
          },
          {
            key: "workgroupId",
            type: "string",
            required: true,
            description: "워크그룹 ID",
          },
          {
            key: "parentFolderId",
            type: "string",
            description: "부모 폴더 ID (루트 생성 시 생략)",
          },
        ],
        response: [
          { key: "data", type: "object", description: "생성된 폴더 정보" },
          { key: "data.folderId", type: "string", description: "생성된 폴더 ID" },
          { key: "data.name", type: "string", description: "폴더 이름" },
          { key: "data.workgroupId", type: "string", description: "워크그룹 ID" },
        ],
        responseType: "json",
      },
      {
        name: "updateFolder",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/folder/update",
        params: [
          {
            key: "folderId",
            type: "string",
            required: true,
            description: "수정할 폴더 ID",
          },
          {
            key: "name",
            type: "string",
            required: true,
            description: "변경할 폴더 이름",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "deleteFolder",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/folder/delete",
        params: [
          {
            key: "folderId",
            type: "string",
            required: true,
            description: "삭제할 폴더 ID",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "deliveryDoc",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/upload/{uniqueId}/delivery",
        params: [
          { key: "syncIds", type: "string[]", required: true, description: "배송할 문서 ID 목록" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "uploadRevision",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/upload/{uid}/revision/{syncId}",
        response: [
          { key: "data", type: "object", description: "업로드된 리비전 파일 정보" },
          { key: "data.fileId", type: "string", description: "파일 ID" },
          { key: "data.fileName", type: "string", description: "파일 이름" },
        ],
        responseType: "json",
      },
      {
        name: "completeUpload",
        method: "POST",
        version: "3.0",
        endpoint: "/wapi/contents/upload/{uid}/complete",
        params: [
          { key: "workgroupId", type: "string", required: true, description: "워크그룹 ID" },
          { key: "folderId", type: "string", description: "업로드할 폴더 ID" },
          { key: "isMultiple", type: "boolean", description: "다중 파일 업로드 여부" },
        ],
        response: [
          { key: "data", type: "object", description: "업로드 완료된 문서 정보" },
          { key: "data.syncId", type: "string", description: "생성된 문서 고유 ID" },
          { key: "data.contentName", type: "string", description: "문서 이름" },
          { key: "data.revisionNo", type: "number", description: "리비전 번호" },
        ],
        responseType: "json",
      },
      {
        name: "completeUpload (with syncId)",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/upload/{uid}/complete/{syncId}",
        params: [
          { key: "workgroupId", type: "string", required: true, description: "워크그룹 ID" },
          { key: "folderId", type: "string", description: "업로드할 폴더 ID" },
        ],
        response: [
          { key: "data", type: "object", description: "업로드 완료된 문서 정보" },
          { key: "data.syncId", type: "string", description: "문서 고유 ID" },
          { key: "data.revisionNo", type: "number", description: "새 리비전 번호" },
        ],
        responseType: "json",
      },
      {
        name: "completeDelivery",
        method: "POST",
        version: "3.0",
        endpoint: "/wapi/contents/upload/{uid}/complete/delivery",
        params: [
          { key: "workgroupId", type: "string", required: true, description: "워크그룹 ID" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getDocSyncInfo",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/contents/syncInfo/{workgroupId}/{syncId}",
        response: [
          { key: "data", type: "object", description: "문서 동기화 정보" },
          { key: "data.syncId", type: "string", description: "문서 고유 ID" },
          { key: "data.lastSyncDate", type: "string", description: "마지막 동기화 일시" },
          { key: "data.syncStatus", type: "string", description: "동기화 상태" },
        ],
        responseType: "json",
      },
      {
        name: "webCheckin",
        method: "POST",
        version: "~2.9",
        endpoint:
          "/wapi/contents/webcheckin/{workgroupId}/{syncId}/{revisionNo}",
        response: [
        ],
        responseType: "json",
      },
      {
        name: "forceWebCheckin",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/forceWebCheckin/{syncId}",
        response: [
        ],
        responseType: "json",
      },
      {
        name: "aiChat",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/ai/chat",
        params: [
          {
            key: "syncId",
            type: "string",
            required: true,
            description: "대화 대상 문서 ID",
          },
          {
            key: "workgroupId",
            type: "string",
            required: true,
            description: "워크그룹 ID",
          },
          {
            key: "message",
            type: "string",
            required: true,
            description: "사용자 입력 메시지",
          },
          {
            key: "convoId",
            type: "string",
            description: "이어서 대화할 세션 ID (신규 대화 시 생략)",
          },
        ],
        response: [
          { key: "data", type: "object", description: "AI 응답 정보" },
          { key: "data.convoId", type: "string", description: "대화 세션 ID" },
          { key: "data.answer", type: "string", description: "AI 응답 내용" },
          { key: "data.status", type: "string", description: "응답 상태 (processing / done)" },
        ],
        responseType: "json",
      },
      {
        name: "aiChatEnd",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/ai/chat/end",
        params: [
          {
            key: "convoId",
            type: "string",
            required: true,
            description: "종료할 AI 대화 세션 ID",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "aiChatCancel",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/ai/chat/cancel",
        params: [
          {
            key: "convoId",
            type: "string",
            required: true,
            description: "취소할 AI 대화 세션 ID",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "aiCheckPermission",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/ai/check/permission",
        params: [
          {
            key: "syncIds",
            type: "string[]",
            required: true,
            description: "AI 사용 권한 확인할 문서 ID 목록",
          },
        ],
        response: [
          { key: "data", type: "object", description: "AI 권한 확인 결과" },
          { key: "data", type: "object", description: "syncId별 AI 사용 가능 여부 맵 (syncId → boolean)" },
        ],
        responseType: "json",
      },
      {
        name: "aiCheckAnswer",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/ai/chat/check-answer",
        params: [
          {
            key: "convoId",
            type: "string",
            required: true,
            description: "응답 확인할 AI 대화 세션 ID",
          },
        ],
        response: [
          { key: "data", type: "object", description: "AI 응답 상태 정보" },
          { key: "data.status", type: "string", description: "응답 상태 (processing / done)" },
          { key: "data.answer", type: "string", description: "AI 응답 내용 (완료 시)" },
        ],
        responseType: "json",
      },
      {
        name: "getFolderInfo",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/contents/folder/info/{folderId}",
        response: [
          { key: "data", type: "object", description: "폴더 정보" },
          { key: "data.folderId", type: "string", description: "폴더 ID" },
          { key: "data.name", type: "string", description: "폴더 이름" },
          { key: "data.workgroupId", type: "string", description: "워크그룹 ID" },
          { key: "data.parentFolderId", type: "string", description: "상위 폴더 ID" },
          { key: "data.fileCount", type: "number", description: "폴더 내 파일 수" },
        ],
        responseType: "json",
      },
      {
        name: "updateFolderFiles",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/contents/folder/file/update",
        params: [
          {
            key: "folderId",
            type: "string",
            required: true,
            description: "폴더 ID",
          },
          {
            key: "syncIds",
            type: "string[]",
            required: true,
            description: "폴더에 추가할 문서 ID 목록",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getUploadDocsProgress",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/contents/upload/{uid}",
        response: [
          { key: "data", type: "object", description: "업로드 진행 정보" },
          { key: "data.uid", type: "string", description: "업로드 세션 UID" },
          { key: "data.status", type: "string", description: "업로드 상태" },
          { key: "data.files", type: "object", description: "업로드된 파일 목록" },
          { key: "data.totalCount", type: "number", description: "전체 파일 수" },
          { key: "data.completedCount", type: "number", description: "완료된 파일 수" },
        ],
        responseType: "json",
      },
      {
        name: "getDocTimeline",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/contents/detail/timeline/{workgroupId}/{syncId}",
        response: [
          { key: "data", type: "object[]", description: "문서 타임라인(리비전) 목록" },
          { key: "data[].revisionNo", type: "number", description: "리비전 번호" },
          { key: "data[].userId", type: "string", description: "작업 사용자 ID" },
          { key: "data[].action", type: "string", description: "작업 유형 (upload, checkout 등)" },
          { key: "data[].createdDate", type: "string", description: "작업 일시" },
          { key: "data[].fileSize", type: "number", description: "해당 리비전 파일 크기" },
        ],
        responseType: "json",
      },
      {
        name: "getDocUserViewCount",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/contents/viewcount/{workgroupId}/{syncId}",
        response: [
          { key: "data", type: "object", description: "문서 조회 수 정보" },
          { key: "data.syncId", type: "string", description: "문서 고유 ID" },
          { key: "data.viewCount", type: "number", description: "전체 조회 수" },
          { key: "data.uniqueUserCount", type: "number", description: "고유 사용자 조회 수" },
        ],
        responseType: "json",
      },
    ],
  },
  {
    id: "login",
    label: "로그인·인증",
    section: "user",
    apis: [
      {
        name: "newSession",
        method: "POST",
        version: "~2.9",
        endpoint: "/sessions/new",
        response: [
          { key: "data", type: "object", description: "세션 정보" },
          { key: "data.sessionId", type: "string", description: "세션 ID" },
          { key: "data.csrfToken", type: "string", description: "CSRF 토큰" },
        ],
        responseType: "json",
      },
      {
        name: "login",
        method: "POST",
        version: "~2.9",
        endpoint: "/sessions/login",
        params: [
          { key: "userId", type: "string", required: true },
          { key: "password", type: "string", required: true },
          { key: "otp", type: "string" },
        ],
        response: [
          { key: "data", type: "object", description: "인증 정보 객체" },
          { key: "data.token", type: "string", description: "API 인증 토큰" },
          { key: "data.userId", type: "string", description: "사용자 ID" },
          { key: "data.userName", type: "string", description: "사용자 이름" },
          { key: "data.dsdCode", type: "string", description: "워크스페이스 코드" },
          { key: "data.expiredTime", type: "number", description: "토큰 만료 시간 (ms)" },
        ],
        responseType: "json",
      },
      {
        name: "getLogInByUid",
        method: "POST",
        version: "~2.9",
        endpoint: "/sessions/uid",
        params: [{ key: "uid", type: "string", required: true }],
        response: [
          { key: "data", type: "object", description: "인증 정보 객체" },
          { key: "data.token", type: "string", description: "API 인증 토큰" },
          { key: "data.userId", type: "string", description: "사용자 ID" },
          { key: "data.userName", type: "string", description: "사용자 이름" },
          { key: "data.dsdCode", type: "string", description: "워크스페이스 코드" },
          { key: "data.expiredTime", type: "number", description: "토큰 만료 시간 (ms)" },
        ],
        responseType: "json",
      },
      {
        name: "logout",
        method: "POST",
        version: "~2.9",
        endpoint: "/sessions/logout",
        response: [
        ],
        responseType: "json",
      },
      {
        name: "loginByOtp (new)",
        method: "POST",
        version: "~2.9",
        endpoint: "/sessions/otp/new",
        params: [
          {
            key: "userId",
            type: "string",
            required: true,
            description: "사용자 ID",
          },
        ],
        response: [
          { key: "data.otpType", type: "string", description: "OTP 방식 (sms / email / google)" },
        ],
        responseType: "json",
      },
      {
        name: "loginByOtp (login)",
        method: "POST",
        version: "~2.9",
        endpoint: "/sessions/otp/login",
        params: [
          { key: "mailOTP", type: "string", required: true, description: "이메일로 받은 OTP 코드" },
          { key: "sessionUid", type: "string", description: "세션 UID" },
        ],
        response: [
          { key: "data", type: "object", description: "인증 정보 객체" },
          { key: "data.token", type: "string", description: "API 인증 토큰" },
          { key: "data.userId", type: "string", description: "사용자 ID" },
          { key: "data.userName", type: "string", description: "사용자 이름" },
          { key: "data.dsdCode", type: "string", description: "워크스페이스 코드" },
          { key: "data.expiredTime", type: "number", description: "토큰 만료 시간 (ms)" },
        ],
        responseType: "json",
      },
      {
        name: "getGoogleOTPInfo (request)",
        method: "POST",
        version: "~2.9",
        endpoint: "/sessions/otp/google/request",
        params: [
          {
            key: "userId",
            type: "string",
            required: true,
            description: "사용자 ID",
          },
        ],
        response: [
          { key: "data", type: "object", description: "Google OTP 등록 정보" },
          { key: "data.qrCodeUrl", type: "string", description: "QR 코드 이미지 URL" },
          { key: "data.secretKey", type: "string", description: "OTP 시크릿 키" },
        ],
        responseType: "json",
      },
      {
        name: "getGoogleOTPInfo (regist)",
        method: "POST",
        version: "~2.9",
        endpoint: "/sessions/otp/google/regist",
        params: [
          { key: "userId", type: "string", required: true, description: "사용자 ID" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getGoogleOTPInfo (login)",
        method: "POST",
        version: "~2.9",
        endpoint: "/sessions/otp/login/google",
        params: [
          { key: "userId", type: "string", required: true, description: "사용자 ID" },
          { key: "otp", type: "string", required: true, description: "Google OTP 인증 코드" },
        ],
        response: [
          { key: "data", type: "object", description: "인증 정보 객체" },
          { key: "data.token", type: "string", description: "API 인증 토큰" },
          { key: "data.userId", type: "string", description: "사용자 ID" },
          { key: "data.userName", type: "string", description: "사용자 이름" },
          { key: "data.dsdCode", type: "string", description: "워크스페이스 코드" },
          { key: "data.expiredTime", type: "number", description: "토큰 만료 시간 (ms)" },
        ],
        responseType: "json",
      },
      {
        name: "esetOtpStart",
        method: "POST",
        version: "~2.9",
        endpoint: "/sessions/otp/eset/start/authenticate",
        params: [
          { key: "userId", type: "string", required: true, description: "사용자 ID (query string)" },
        ],
        response: [
          { key: "data.challengeToken", type: "string", description: "ESET OTP 챌린지 토큰" },
        ],
        responseType: "json",
      },
      {
        name: "esetOtpAuthenticate",
        method: "POST",
        version: "~2.9",
        endpoint: "/sessions/otp/eset/authenticate",
        params: [
          { key: "pushChallengeId", type: "string", required: true, description: "ESET OTP 챌린지 ID (query string)" },
          { key: "userId", type: "string", required: true, description: "사용자 ID (query string)" },
        ],
        response: [
          { key: "data", type: "object", description: "인증 정보 객체" },
          { key: "data.token", type: "string", description: "API 인증 토큰" },
          { key: "data.userId", type: "string", description: "사용자 ID" },
          { key: "data.userName", type: "string", description: "사용자 이름" },
          { key: "data.dsdCode", type: "string", description: "워크스페이스 코드" },
          { key: "data.expiredTime", type: "number", description: "토큰 만료 시간 (ms)" },
        ],
        responseType: "json",
      },
      {
        name: "liteExternalOttInit",
        method: "POST",
        version: "3.0",
        endpoint: "/sessions/lite-external/ott/init",
        params: [
          { key: "userId", type: "string", required: true, description: "외부 사용자 ID (query string)" },
          { key: "sotp", type: "string", description: "SOTP 값 (query string)" },
        ],
        response: [
          { key: "data", type: "object", description: "OTT 초기화 정보" },
          { key: "data.ott", type: "string", description: "일회성 토큰 (OTT)" },
          { key: "data.expiredTime", type: "number", description: "OTT 만료 시간 (ms)" },
        ],
        responseType: "json",
      },
      {
        name: "liteExternalOtt",
        method: "POST",
        version: "~2.9",
        endpoint: "/sessions/lite-external/ott",
        params: [
          { key: "mailOTP", type: "string", required: true, description: "일회성 토큰 OTP (query string)" },
        ],
        response: [
          { key: "data", type: "object", description: "인증 정보 객체" },
          { key: "data.token", type: "string", description: "API 인증 토큰" },
          { key: "data.userId", type: "string", description: "사용자 ID" },
          { key: "data.userName", type: "string", description: "사용자 이름" },
          { key: "data.dsdCode", type: "string", description: "워크스페이스 코드" },
          { key: "data.expiredTime", type: "number", description: "토큰 만료 시간 (ms)" },
        ],
        responseType: "json",
      },
      {
        name: "validateActionUrl",
        method: "POST",
        version: "~2.9",
        endpoint: "/papi/actionUrl/valid",
        params: [
          { key: "uuid", type: "string", required: true, description: "액션 URL의 UUID" },
          { key: "password", type: "string", description: "비밀번호 (설정된 경우)" },
        ],
        response: [
          { key: "data.valid", type: "boolean", description: "액션 URL 유효 여부" },
        ],
        responseType: "json",
      },
      {
        name: "checkActionUrlPermission",
        method: "POST",
        version: "3.0",
        endpoint: "/papi/actionUrl/{uid}/permission",
        params: [
          { key: "userId", type: "string", description: "사용자 ID" },
        ],
        response: [
          { key: "data", type: "object", description: "액션 URL 권한 정보" },
          { key: "data.viewable", type: "boolean", description: "열람 가능 여부" },
          { key: "data.downloadable", type: "boolean", description: "다운로드 가능 여부" },
          { key: "data.printable", type: "boolean", description: "인쇄 가능 여부" },
        ],
        responseType: "json",
      },
      {
        name: "getInformationFromUid",
        method: "POST",
        version: "~2.9",
        endpoint: "/papi/actionUrl/{uid}/info",
        response: [
          { key: "data", type: "object", description: "UID 기반 문서 정보" },
          { key: "data.syncId", type: "string", description: "문서 고유 ID" },
          { key: "data.contentName", type: "string", description: "문서 이름" },
          { key: "data.workgroupId", type: "string", description: "워크그룹 ID" },
        ],
        responseType: "json",
      },
      {
        name: "getInformationFromSyncId",
        method: "GET",
        version: "3.0",
        endpoint: "/papi/actionUrl/syncId/{syncId}/info",
        response: [
          { key: "data", type: "object", description: "syncId 기반 문서 정보" },
          { key: "data.syncId", type: "string", description: "문서 고유 ID" },
          { key: "data.contentName", type: "string", description: "문서 이름" },
          { key: "data.uid", type: "string", description: "액션 URL UID" },
        ],
        responseType: "json",
      },
      {
        name: "getFileUrlFromUid",
        method: "GET",
        version: "~2.9",
        endpoint: "/papi/file/{uid}",
        response: [
          { key: "data", type: "object", description: "파일 URL 정보" },
          { key: "data.fileUrl", type: "string", description: "파일 접근 URL" },
          { key: "data.contentName", type: "string", description: "파일 이름" },
        ],
        responseType: "json",
      },
      {
        name: "getFileUrlDownloadFromUid",
        method: "GET",
        version: "~2.9",
        endpoint: "/papi/file/{uid}/download",
        responseType: "blob",
      },
      {
        name: "getFileSimpleInfo",
        method: "GET",
        version: "~2.9",
        endpoint: "/papi/file/detail/{syncId}",
        response: [
          { key: "data", type: "object", description: "파일 간략 정보" },
          { key: "data.syncId", type: "string", description: "문서 고유 ID" },
          { key: "data.contentName", type: "string", description: "파일 이름" },
          { key: "data.fileType", type: "string", description: "파일 확장자" },
          { key: "data.fileSize", type: "number", description: "파일 크기 (bytes)" },
        ],
        responseType: "json",
      },
      {
        name: "initPassword",
        method: "GET",
        version: "~2.9",
        endpoint: "/papi/passwd/init/{userId}",
        response: [
        ],
        responseType: "json",
      },
      {
        name: "updatePassword",
        method: "POST",
        version: "~2.9",
        endpoint: "/papi/passwd/update",
        params: [
          {
            key: "oldPassword",
            type: "string",
            required: true,
            description: "현재 비밀번호",
          },
          {
            key: "newPassword",
            type: "string",
            required: true,
            description: "새 비밀번호",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getAlarmList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/dashboard/myAlarms",
        params: [
          {
            key: "page",
            type: "number",
            description: "페이지 번호 (기본값: 0)",
          },
          { key: "pageSize", type: "number", description: "페이지당 항목 수" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "알람 목록" },
          { key: "data.content[].alarmId", type: "string", description: "알람 ID" },
          { key: "data.content[].message", type: "string", description: "알람 메시지" },
          { key: "data.content[].isRead", type: "boolean", description: "읽음 여부" },
          { key: "data.content[].createdDate", type: "string", description: "생성일시" },
          { key: "data.content[].type", type: "string", description: "알람 유형" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "readAlarm",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/dashboard/readAlarm",
        params: [
          {
            key: "alarmId",
            type: "string",
            required: true,
            description: "읽음 처리할 알람 ID",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "readAllAlarm",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/dashboard/readAllAlarm",
        response: [
        ],
        responseType: "json",
      },
      {
        name: "onceLoginPay",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/tenancy/oncelogin/pay",
        params: [
          { key: "userId", type: "string", required: true },
          { key: "userEmail", type: "string", required: true },
          { key: "userName", type: "string", required: true },
        ],
        response: [
          { key: "data", type: "object", description: "인증 정보 객체" },
          { key: "data.token", type: "string", description: "API 인증 토큰" },
          { key: "data.userId", type: "string", description: "사용자 ID" },
          { key: "data.userName", type: "string", description: "사용자 이름" },
          { key: "data.dsdCode", type: "string", description: "워크스페이스 코드" },
          { key: "data.expiredTime", type: "number", description: "토큰 만료 시간 (ms)" },
        ],
        responseType: "json",
      },
      {
        name: "onceLoginManage",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/tenancy/oncelogin/manage",
        params: [
          { key: "userId", type: "string", required: true },
          { key: "userEmail", type: "string", required: true },
          { key: "userName", type: "string", required: true },
          { key: "alias", type: "string" },
        ],
        response: [
          { key: "data", type: "object", description: "인증 정보 객체" },
          { key: "data.token", type: "string", description: "API 인증 토큰" },
          { key: "data.userId", type: "string", description: "사용자 ID" },
          { key: "data.userName", type: "string", description: "사용자 이름" },
          { key: "data.dsdCode", type: "string", description: "워크스페이스 코드" },
          { key: "data.expiredTime", type: "number", description: "토큰 만료 시간 (ms)" },
        ],
        responseType: "json",
      },
      {
        name: "downloadHtml",
        method: "GET",
        version: "~2.9",
        endpoint:
          "/wapi/contents/download/{uid}/{syncId}/revisions/{revisionNo}/html",
        responseType: "text",
      },
    ],
  },
  {
    id: "workgroup",
    label: "워크그룹",
    section: "user",
    apis: [
      {
        name: "getWorkgroupSimpleList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/workgroups/simpleList",
        params: [{ key: "keyword", type: "string", description: "검색어" }],
        response: [
          { key: "data", type: "object[]", description: "워크그룹 간략 목록" },
          { key: "data[].workgroupId", type: "string", description: "워크그룹 ID" },
          { key: "data[].name", type: "string", description: "워크그룹 이름" },
          { key: "data[].type", type: "string", description: "워크그룹 유형" },
          { key: "data[].favorite", type: "boolean", description: "즐겨찾기 여부" },
        ],
        responseType: "json",
      },
      {
        name: "getWorkgroupSideList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/workgroups/sideList",
        params: [{ key: "keyword", type: "string", description: "검색어" }],
        response: [
          { key: "data", type: "object[]", description: "사이드바용 워크그룹 목록" },
          { key: "data[].workgroupId", type: "string", description: "워크그룹 ID" },
          { key: "data[].name", type: "string", description: "워크그룹 이름" },
          { key: "data[].unreadCount", type: "number", description: "미확인 알림 수" },
        ],
        responseType: "json",
      },
      {
        name: "getOrgansTree",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/organs/tree",
        params: [
          { key: "workgroupId", type: "string", description: "워크그룹 ID" },
        ],
        response: [
          { key: "data", type: "object[]", description: "조직도 트리 구조" },
          { key: "data[].code", type: "string", description: "부서/조직 코드" },
          { key: "data[].name", type: "string", description: "부서/조직 이름" },
          { key: "data[].children", type: "object", description: "하위 조직 배열" },
        ],
        responseType: "json",
      },
      {
        name: "getMemberList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/workgroups/members/searchAll/paging",
        params: [
          {
            key: "workgroupId",
            type: "string",
            required: true,
            description: "워크그룹 ID",
          },
          {
            key: "keyword",
            type: "string",
            description: "검색어 (이름, 이메일 등)",
          },
          { key: "page", type: "number", description: "페이지 번호" },
          { key: "pageSize", type: "number", description: "페이지당 항목 수" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "멤버 목록" },
          { key: "data.content[].userId", type: "string", description: "사용자 ID" },
          { key: "data.content[].userName", type: "string", description: "사용자 이름" },
          { key: "data.content[].email", type: "string", description: "이메일" },
          { key: "data.content[].role", type: "string", description: "워크그룹 역할" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "getWorkgroupFullList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/workgroups/list",
        params: [
          { key: "keyword", type: "string", description: "검색어" },
          { key: "page", type: "number", description: "페이지 번호" },
          { key: "pageSize", type: "number", description: "페이지당 항목 수" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "워크그룹 전체 목록" },
          { key: "data.content[].workgroupId", type: "string", description: "워크그룹 ID" },
          { key: "data.content[].name", type: "string", description: "워크그룹 이름" },
          { key: "data.content[].memberCount", type: "number", description: "멤버 수" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "getTotalFavoriteItems",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/workgroups/favorite/item/list/total",
        response: [
          { key: "data", type: "object", description: "즐겨찾기 전체 목록" },
          { key: "data.workgroups", type: "object", description: "즐겨찾기 워크그룹 목록" },
          { key: "data.docs", type: "object", description: "즐겨찾기 문서 목록" },
        ],
        responseType: "json",
      },
      {
        name: "getFavoriteMeetingList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/workgroups/favorite/meeting/list",
        response: [
          { key: "data", type: "object[]", description: "즐겨찾기 회의 목록" },
          { key: "data[].meetingId", type: "string", description: "회의 ID" },
          { key: "data[].title", type: "string", description: "회의 제목" },
          { key: "data[].startTime", type: "string", description: "회의 시작 시간" },
        ],
        responseType: "json",
      },
      {
        name: "setFavoriteWorkgroups",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/workgroups/favorite",
        params: [
          { key: "workgroupId", type: "string", required: true, description: "즐겨찾기 설정할 워크그룹 ID" },
          { key: "users", type: "object[]", required: true, description: "즐겨찾기 설정 사용자 목록" },
          { key: "users[].userId", type: "string", required: true, description: "사용자 ID" },
          { key: "users[].favorite", type: "string", required: true, description: "즐겨찾기 여부 (Y/N)" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getWorkgroupDetail",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/workgroups",
        params: [{ key: "workgroupId", type: "string", required: true }],
        response: [
          { key: "data", type: "object", description: "워크그룹 상세 정보" },
          { key: "data.workgroupId", type: "string", description: "워크그룹 ID" },
          { key: "data.name", type: "string", description: "워크그룹 이름" },
          { key: "data.description", type: "string", description: "설명" },
          { key: "data.type", type: "string", description: "워크그룹 유형" },
          { key: "data.status", type: "number", description: "상태" },
          { key: "data.memberCount", type: "number", description: "멤버 수" },
          { key: "data.createdDate", type: "string", description: "생성일시" },
        ],
        responseType: "json",
      },
      {
        name: "addMembers",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/workgroups/members",
        params: [{ key: "userId", type: "string", required: true }],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "updateMembers",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/workgroups/members/update",
        params: [{ key: "users", type: "object", required: true }],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getWorkspaceConfig",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/tenancy/config",
        response: [
          { key: "data", type: "object", description: "워크스페이스 설정 정보" },
          { key: "data.maxFileSize", type: "number", description: "최대 파일 크기 (bytes)" },
          { key: "data.allowedExtensions", type: "string[]", description: "허용 파일 확장자 목록" },
          { key: "data.language", type: "string", description: "기본 언어" },
          { key: "data.storageQuota", type: "number", description: "스토리지 할당량 (bytes)" },
        ],
        responseType: "json",
      },
      {
        name: "createWorkgroup",
        method: "POST",
        version: "3.0",
        endpoint: "/wapi/workgroups/insert",
        params: [
          {
            key: "name",
            type: "string",
            required: true,
            description: "워크그룹 이름",
          },
          { key: "description", type: "string", description: "워크그룹 설명" },
          { key: "type", type: "string", description: "워크그룹 타입" },
        ],
        response: [
          { key: "data", type: "object", description: "생성된 워크그룹 정보" },
          { key: "data.workgroupId", type: "string", description: "생성된 워크그룹 ID" },
          { key: "data.name", type: "string", description: "워크그룹 이름" },
        ],
        responseType: "json",
      },
      {
        name: "createPrivateWorkgroup",
        method: "POST",
        version: "3.0",
        endpoint: "/wapi/workgroups/insert/private",
        params: [
          {
            key: "name",
            type: "string",
            required: true,
            description: "개인 워크그룹 이름",
          },
        ],
        response: [
          { key: "data", type: "object", description: "생성된 개인 워크그룹 정보" },
          { key: "data.workgroupId", type: "string", description: "생성된 워크그룹 ID" },
          { key: "data.name", type: "string", description: "워크그룹 이름" },
        ],
        responseType: "json",
      },
      {
        name: "updateWorkgroup",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/workgroups/update",
        params: [
          {
            key: "workgroupId",
            type: "string",
            required: true,
            description: "수정할 워크그룹 ID",
          },
          { key: "name", type: "string", description: "변경할 워크그룹 이름" },
          {
            key: "description",
            type: "string",
            description: "변경할 워크그룹 설명",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "resendMemberInvite",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/workgroups/members/resend/{workgroupId}/{userId}",
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getWorkgroupNoti",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/policy/notification/{workgroupId}",
        response: [
          { key: "data", type: "object", description: "워크그룹 알림 설정" },
          { key: "data.email", type: "boolean", description: "이메일 알림 활성화 여부" },
          { key: "data.push", type: "boolean", description: "푸시 알림 활성화 여부" },
        ],
        responseType: "json",
      },
      {
        name: "updateWorkgroupNoti",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/policy/notification/{workgroupId}",
        params: [
          { key: "workgroupId", type: "string", required: true, description: "워크그룹 ID" },
          { key: "notificationPolicy", type: "object", required: true, description: "알림 정책 객체" },
          { key: "notificationPolicy.email", type: "boolean", description: "이메일 알림 활성화 여부" },
          { key: "notificationPolicy.push", type: "boolean", description: "푸시 알림 활성화 여부" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "updateWorkgroupStatus",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/workgroups/update/status",
        params: [
          { key: "workgroupId", type: "string", required: true },
          { key: "status", type: "number", required: true },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "withdrawMember",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/workgroups/members/withdraw",
        params: [
          { key: "workgroupId", type: "string", required: true, description: "워크그룹 ID" },
          { key: "users", type: "object[]", required: true, description: "탈퇴 처리 사용자 목록" },
          { key: "users[].userId", type: "string", required: true, description: "사용자 ID" },
          { key: "users[].userType", type: "number", description: "사용자 유형" },
          { key: "users[].withdrawYN", type: "string", description: "탈퇴 여부 (Y/N)" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getWorkgroupHomeDashboard",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/workgroups/home/dashboard/{workgroupId}",
        response: [
          { key: "data", type: "object", description: "워크그룹 홈 대시보드 데이터" },
          { key: "data.recentDocs", type: "object", description: "최근 문서 목록" },
          { key: "data.memberCount", type: "number", description: "멤버 수" },
          { key: "data.docCount", type: "number", description: "문서 수" },
          { key: "data.storageUsed", type: "number", description: "사용 스토리지 (bytes)" },
        ],
        responseType: "json",
      },
      {
        name: "getMyDashboardTasks",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/dashboard/tasks",
        response: [
          { key: "data", type: "object[]", description: "나의 할 일 목록" },
          { key: "data[].taskId", type: "string", description: "태스크 ID" },
          { key: "data[].title", type: "string", description: "태스크 제목" },
          { key: "data[].dueDate", type: "string", description: "마감일" },
          { key: "data[].status", type: "string", description: "상태" },
        ],
        responseType: "json",
      },
      {
        name: "getMyDashboardSuggestion",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/dashboard/suggestions",
        response: [
          { key: "data", type: "object[]", description: "추천 문서/워크그룹 목록" },
          { key: "data[].type", type: "string", description: "추천 항목 유형 (doc / workgroup)" },
          { key: "data[].id", type: "string", description: "항목 ID" },
          { key: "data[].name", type: "string", description: "항목 이름" },
        ],
        responseType: "json",
      },
      {
        name: "getWorkgroupCalendarCount",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/workgroups/calendar/{workgroupId}/count",
        params: [
          { key: "date", type: "string", description: "기준 날짜 (YYYY-MM-DD)" },
          { key: "startDate", type: "string", description: "조회 시작일 (YYYY-MM-DD)" },
          { key: "endDate", type: "string", description: "조회 종료일 (YYYY-MM-DD)" },
          { key: "offset", type: "number", description: "타임존 오프셋" },
        ],
        response: [
          { key: "data", type: "object", description: "날짜별 이벤트 수" },
          { key: "data", type: "object", description: "날짜(YYYY-MM-DD) → 이벤트 수 맵" },
        ],
        responseType: "json",
      },
      {
        name: "getWorkgroupCalendarDetail",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/workgroups/calendar/{workgroupId}/detail",
        params: [
          { key: "date", type: "number", description: "기준 날짜 (timestamp)" },
        ],
        response: [
          { key: "data", type: "object[]", description: "해당 날짜 이벤트 목록" },
          { key: "data[].eventId", type: "string", description: "이벤트 ID" },
          { key: "data[].title", type: "string", description: "이벤트 제목" },
          { key: "data[].startTime", type: "string", description: "시작 시간" },
          { key: "data[].type", type: "string", description: "이벤트 유형 (meeting 등)" },
        ],
        responseType: "json",
      },
      {
        name: "getRecentHistoryWorkgroupList",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/workgroups/recent/list",
        params: [
          { key: "startDate", type: "number", description: "조회 시작 timestamp" },
          { key: "endDate", type: "number", description: "조회 종료 timestamp" },
        ],
        response: [
          { key: "data", type: "object[]", description: "최근 방문 워크그룹 목록" },
          { key: "data[].workgroupId", type: "string", description: "워크그룹 ID" },
          { key: "data[].name", type: "string", description: "워크그룹 이름" },
          { key: "data[].visitedDate", type: "string", description: "마지막 방문 일시" },
        ],
        responseType: "json",
      },
      {
        name: "deleteMember",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/workgroups/members/delete/user",
        params: [{ key: "userId", type: "string", required: true }],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "checkoutMember",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/workgroups/members/checkout",
        params: [
          { key: "userIds", type: "string[]", required: true },
          { key: "workgroupId", type: "string" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getNestedWorkgroupList",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/workgroups/tree",
        response: [
          { key: "data", type: "object[]", description: "중첩 워크그룹 트리" },
          { key: "data[].workgroupId", type: "string", description: "워크그룹 ID" },
          { key: "data[].name", type: "string", description: "워크그룹 이름" },
          { key: "data[].children", type: "object", description: "하위 워크그룹 배열" },
        ],
        responseType: "json",
      },
      {
        name: "moveWorkgroup",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/workgroups/update/tree",
        params: [
          { key: "workgroupId", type: "string", required: true },
          { key: "targetParentWorkgroupId", type: "string", required: true },
        ],
        response: [
        ],
        responseType: "json",
      },
    ],
  },
  {
    id: "workspace",
    label: "워크스페이스",
    section: "user",
    apis: [
      {
        name: "getWorkspaceList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/tenancy/list/my",
        params: [{ key: "userId", type: "string", required: true }],
        response: [
          { key: "data", type: "object[]", description: "워크스페이스 목록" },
          { key: "data[].dsdCode", type: "string", description: "워크스페이스 코드" },
          { key: "data[].name", type: "string", description: "워크스페이스 이름" },
          { key: "data[].status", type: "number", description: "상태" },
          { key: "data[].role", type: "string", description: "내 역할" },
        ],
        responseType: "json",
      },
      {
        name: "getWorkspaceDetail",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/tenancy/info/{dsdCode}",
        response: [
          { key: "data", type: "object", description: "워크스페이스 상세 정보" },
          { key: "data.dsdCode", type: "string", description: "워크스페이스 코드" },
          { key: "data.name", type: "string", description: "워크스페이스 이름" },
          { key: "data.status", type: "number", description: "상태" },
          { key: "data.storageQuota", type: "number", description: "스토리지 할당량 (bytes)" },
          { key: "data.storageUsed", type: "number", description: "사용 스토리지 (bytes)" },
          { key: "data.memberCount", type: "number", description: "멤버 수" },
        ],
        responseType: "json",
      },
      {
        name: "getWorkspacePayMyList",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/tenancy/pay/payment/mylist",
        response: [
          { key: "data", type: "object[]", description: "내 결제 목록" },
          { key: "data[].paymentId", type: "string", description: "결제 ID" },
          { key: "data[].plan", type: "string", description: "플랜 이름" },
          { key: "data[].status", type: "string", description: "결제 상태" },
          { key: "data[].expiredDate", type: "string", description: "만료일" },
        ],
        responseType: "json",
      },
      {
        name: "getWorkspacePayMyHistory",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/tenancy/pay/payment/myHistory",
        response: [
          { key: "data", type: "object[]", description: "결제 내역 목록" },
          { key: "data[].paymentId", type: "string", description: "결제 ID" },
          { key: "data[].amount", type: "number", description: "결제 금액" },
          { key: "data[].paidDate", type: "string", description: "결제일" },
          { key: "data[].plan", type: "string", description: "플랜 이름" },
        ],
        responseType: "json",
      },
      {
        name: "getWorkspacePayPlanList",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/tenancy/pay/payment/plans",
        response: [
          { key: "data", type: "object[]", description: "결제 플랜 목록" },
          { key: "data[].planId", type: "string", description: "플랜 ID" },
          { key: "data[].name", type: "string", description: "플랜 이름" },
          { key: "data[].price", type: "number", description: "가격" },
          { key: "data[].storageQuota", type: "number", description: "스토리지 할당량 (bytes)" },
          { key: "data[].maxMembers", type: "number", description: "최대 멤버 수" },
        ],
        responseType: "json",
      },
      {
        name: "createWorkspace",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/tenancy/create",
        params: [
          {
            key: "name",
            type: "string",
            required: true,
            description: "워크스페이스 이름",
          },
          {
            key: "description",
            type: "string",
            description: "워크스페이스 설명",
          },
        ],
        response: [
          { key: "data", type: "object", description: "생성된 워크스페이스 정보" },
          { key: "data.dsdCode", type: "string", description: "워크스페이스 코드" },
          { key: "data.name", type: "string", description: "워크스페이스 이름" },
        ],
        responseType: "json",
      },
      {
        name: "updateWorkspaceStatus",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/tenancy/status",
        params: [
          {
            key: "dsdCode",
            type: "string",
            required: true,
            description: "워크스페이스 코드",
          },
          {
            key: "status",
            type: "number",
            required: true,
            description: "변경할 상태값",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "deleteWorkspace",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/tenancy/delete",
        params: [
          {
            key: "dsdCode",
            type: "string",
            required: true,
            description: "삭제할 워크스페이스 코드",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getWorkspaceMemberList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/tenancy/member/list",
        params: [
          { key: "searchOpt", type: "string", description: "검색어" },
          { key: "dsdCode", type: "string", description: "워크스페이스 코드" },
          { key: "userType", type: "number", description: "사용자 유형 필터" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "워크스페이스 멤버 목록" },
          { key: "data.content[].userId", type: "string", description: "사용자 ID" },
          { key: "data.content[].userName", type: "string", description: "사용자 이름" },
          { key: "data.content[].email", type: "string", description: "이메일" },
          { key: "data.content[].role", type: "string", description: "역할" },
          { key: "data.content[].status", type: "number", description: "상태" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "getWorkspaceMemberList (admin)",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/tenancy/member/admin/list",
        params: [
          { key: "searchOpt", type: "string", description: "검색어" },
          { key: "dsdCode", type: "string", description: "워크스페이스 코드" },
          { key: "isAdminPage", type: "boolean", description: "관리자 페이지 여부" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "워크스페이스 관리자 멤버 목록" },
          { key: "data.content[].userId", type: "string", description: "사용자 ID" },
          { key: "data.content[].userName", type: "string", description: "사용자 이름" },
          { key: "data.content[].email", type: "string", description: "이메일" },
          { key: "data.content[].role", type: "string", description: "역할" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "inviteMember",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/tenancy/member/invite",
        params: [
          { key: "userId", type: "string", required: true },
          { key: "userEmail", type: "string", required: true },
          { key: "userName", type: "string" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "resendInvite",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/tenancy/member/invite/resend",
        params: [
          { key: "userId", type: "string", required: true },
          { key: "dsdCode", type: "string", required: true },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "updateMember",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/tenancy/member/update",
        params: [
          { key: "userId", type: "string", required: true },
          { key: "userType", type: "number" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "deleteMember",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/tenancy/member/delete",
        params: [
          { key: "userIds", type: "string[]", required: true },
          { key: "dsdCode", type: "string", required: true },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "registerVerify",
        method: "POST",
        version: "~2.9",
        endpoint: "/papi/tenancy/member/register/verify",
        params: [
          { key: "userId", type: "string", required: true, description: "사용자 ID" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "registerVerifyResend",
        method: "POST",
        version: "~2.9",
        endpoint: "/papi/tenancy/member/register/verify/resend",
        params: [
          { key: "userId", type: "string", required: true, description: "사용자 ID" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getWorkspaceConfig",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/tenancy/config",
        response: [
          { key: "data", type: "object", description: "워크스페이스 설정 정보" },
          { key: "data.maxFileSize", type: "number", description: "최대 파일 크기 (bytes)" },
          { key: "data.allowedExtensions", type: "string[]", description: "허용 파일 확장자 목록" },
          { key: "data.language", type: "string", description: "기본 언어" },
          { key: "data.storageQuota", type: "number", description: "스토리지 할당량 (bytes)" },
        ],
        responseType: "json",
      },
      {
        name: "getWorkspaceUserInfo",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/tenancy/info/my",
        response: [
          { key: "data", type: "object", description: "내 워크스페이스 정보" },
          { key: "data.userId", type: "string", description: "사용자 ID" },
          { key: "data.userName", type: "string", description: "사용자 이름" },
          { key: "data.email", type: "string", description: "이메일" },
          { key: "data.role", type: "string", description: "역할" },
          { key: "data.dsdCode", type: "string", description: "워크스페이스 코드" },
        ],
        responseType: "json",
      },
    ],
  },
  {
    id: "policy",
    label: "정책",
    section: "user",
    apis: [
      {
        name: "executePolicy",
        method: "POST",
        version: "3.0",
        endpoint: "/wapi/policy/{syncId}/{workgroupId}/execute",
        response: [
          { key: "data", type: "object", description: "정책 실행 결과" },
          { key: "data.policyId", type: "string", description: "적용된 정책 ID" },
          { key: "data.appliedAt", type: "string", description: "적용 일시" },
        ],
        responseType: "json",
      },
      {
        name: "getDefaultPolicy",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/policy/{syncId}/{workgroupId}",
        response: [
          { key: "data", type: "object", description: "기본 정책 정보" },
          { key: "data.viewable", type: "boolean", description: "열람 가능 여부" },
          { key: "data.downloadable", type: "boolean", description: "다운로드 가능 여부" },
          { key: "data.printable", type: "boolean", description: "인쇄 가능 여부" },
          { key: "data.expireDate", type: "string", description: "만료일" },
        ],
        responseType: "json",
      },
      {
        name: "getPolicy",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/policy/{workgroupId}/{syncId}",
        response: [
          { key: "data", type: "object", description: "문서 정책 정보" },
          { key: "data.viewable", type: "boolean", description: "열람 가능 여부" },
          { key: "data.downloadable", type: "boolean", description: "다운로드 가능 여부" },
          { key: "data.printable", type: "boolean", description: "인쇄 가능 여부" },
          { key: "data.expireDate", type: "string", description: "만료일" },
          { key: "data.watermark", type: "boolean", description: "워터마크 여부" },
        ],
        responseType: "json",
      },
      {
        name: "resetPolicy",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/policy/reset/{syncId}/{workgroupId}",
        response: [
        ],
        responseType: "json",
      },
      {
        name: "setFileLinkPolicy",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/policy/actionUrl/{workgroupId}/{syncId}",
        params: [
          { key: "fileShareLinkPolicy", type: "object", required: true, description: "파일 공유 링크 정책 객체" },
          { key: "fileShareLinkPolicy.expireDate", type: "string", description: "링크 만료일 (YYYY-MM-DD)" },
          { key: "fileShareLinkPolicy.downloadable", type: "boolean", description: "다운로드 허용 여부" },
          { key: "fileShareLinkPolicy.printable", type: "boolean", description: "인쇄 허용 여부" },
        ],
        response: [
          { key: "data", type: "object", description: "파일 링크 정책 정보" },
          { key: "data.uid", type: "string", description: "생성된 액션 URL UID" },
          { key: "data.url", type: "string", description: "공유 링크 URL" },
          { key: "data.expireDate", type: "string", description: "링크 만료일" },
        ],
        responseType: "json",
      },
      {
        name: "getFileLinkPolicy",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/policy/actionUrl/{workgroupId}/{syncId}",
        response: [
          { key: "data", type: "object", description: "파일 링크 정책 정보" },
          { key: "data.uid", type: "string", description: "액션 URL UID" },
          { key: "data.expireDate", type: "string", description: "링크 만료일" },
          { key: "data.downloadable", type: "boolean", description: "다운로드 허용 여부" },
          { key: "data.printable", type: "boolean", description: "인쇄 허용 여부" },
        ],
        responseType: "json",
      },
      {
        name: "getPolicyNoti",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/policy/notification/myInfo",
        response: [
          { key: "data", type: "object", description: "정책 알림 설정" },
          { key: "data.email", type: "boolean", description: "이메일 알림 활성화 여부" },
          { key: "data.push", type: "boolean", description: "푸시 알림 활성화 여부" },
        ],
        responseType: "json",
      },
      {
        name: "updatePolicyNoti",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/policy/notification/myInfo",
        params: [
          { key: "workgroupId", type: "string", description: "워크그룹 ID" },
          { key: "notificationPolicy", type: "object", required: true, description: "알림 정책 객체" },
          { key: "notificationPolicy.email", type: "boolean", description: "이메일 알림 활성화 여부" },
          { key: "notificationPolicy.push", type: "boolean", description: "푸시 알림 활성화 여부" },
        ],
        response: [
        ],
        responseType: "json",
      },
    ],
  },
  {
    id: "app",
    label: "앱 연동",
    section: "user",
    apis: [
      {
        name: "getAppLoginPage",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/{type}/registUrl",
        response: [
          { key: "data", type: "object", description: "앱 로그인 페이지 정보" },
          { key: "data.url", type: "string", description: "앱 인증 페이지 URL" },
        ],
        responseType: "json",
      },
      {
        name: "getAppLoginPage (oauth2)",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/oauth2/{type}/registUrl",
        response: [
          { key: "data", type: "object", description: "OAuth2 앱 로그인 페이지 정보" },
          { key: "data.url", type: "string", description: "OAuth2 인증 페이지 URL" },
        ],
        responseType: "json",
      },
      {
        name: "unregisterApp",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/{type}/unregist",
        response: [
        ],
        responseType: "json",
      },
      {
        name: "revokeOAuth2App",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/oauth2/{type}/revoke",
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getAppLoginInfo",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/{type}/myInfo",
        response: [
          { key: "data", type: "object", description: "연동된 앱 정보" },
          { key: "data.connected", type: "boolean", description: "연동 여부" },
          { key: "data.accountName", type: "string", description: "연동된 계정명" },
          { key: "data.type", type: "string", description: "앱 유형" },
        ],
        responseType: "json",
      },
      {
        name: "getDropboxList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/oauth2/dropbox/files",
        params: [
          {
            key: "path",
            type: "string",
            description: "Dropbox 폴더 경로 (기본값: 루트)",
          },
          { key: "cursor", type: "string", description: "페이지네이션 커서" },
        ],
        response: [
          { key: "data", type: "object", description: "Dropbox 파일 목록" },
          { key: "data.entries", type: "object[]", description: "파일/폴더 항목 배열" },
          { key: "data.entries[].id", type: "string", description: "항목 ID" },
          { key: "data.entries[].name", type: "string", description: "파일/폴더 이름" },
          { key: "data.entries[].type", type: "string", description: "유형 (file / folder)" },
          { key: "data.cursor", type: "string", description: "다음 페이지 커서" },
          { key: "data.hasMore", type: "boolean", description: "다음 페이지 존재 여부" },
        ],
        responseType: "json",
      },
      {
        name: "downloadDropboxFile",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/oauth2/dropbox/files/{fileId}/download/{uid}",
        responseType: "blob",
      },
      {
        name: "uploadToCloud",
        method: "GET",
        version: "~2.9",
        endpoint:
          "/wapi/oauth2/{type}/files/upload/{syncId}/revisions/{revisionNo}",
        response: [
          { key: "data", type: "object", description: "클라우드 업로드 결과" },
          { key: "data.uploadedUrl", type: "string", description: "업로드된 파일 URL" },
        ],
        responseType: "json",
      },
    ],
  },
  {
    id: "log",
    label: "로그",
    section: "user",
    apis: [
      {
        name: "getLogList",
        method: "POST",
        version: "3.0",
        endpoint: "/wapi/logs/new/paging",
        params: [
          { key: "workgroupId", type: "string", description: "워크그룹 ID" },
          { key: "page", type: "number", description: "페이지 번호" },
          { key: "pageSize", type: "number", description: "페이지당 항목 수" },
          {
            key: "startDate",
            type: "string",
            description: "조회 시작일 (YYYY-MM-DD)",
          },
          {
            key: "endDate",
            type: "string",
            description: "조회 종료일 (YYYY-MM-DD)",
          },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "로그 목록" },
          { key: "data.content[].logId", type: "string", description: "로그 ID" },
          { key: "data.content[].userId", type: "string", description: "사용자 ID" },
          { key: "data.content[].action", type: "string", description: "액션 유형" },
          { key: "data.content[].syncId", type: "string", description: "문서 ID" },
          { key: "data.content[].createdDate", type: "string", description: "발생 일시" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "getSummaryLogList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/logs/summary",
        params: [
          { key: "workgroupId", type: "string", description: "워크그룹 ID" },
          {
            key: "startDate",
            type: "string",
            description: "조회 시작일 (YYYY-MM-DD)",
          },
          {
            key: "endDate",
            type: "string",
            description: "조회 종료일 (YYYY-MM-DD)",
          },
        ],
        response: [
          { key: "data", type: "object[]", description: "로그 요약 목록" },
          { key: "data[].date", type: "string", description: "날짜 (YYYY-MM-DD)" },
          { key: "data[].viewCount", type: "number", description: "열람 수" },
          { key: "data[].downloadCount", type: "number", description: "다운로드 수" },
          { key: "data[].uploadCount", type: "number", description: "업로드 수" },
        ],
        responseType: "json",
      },
      {
        name: "getLogDetail",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/logs/detail",
        params: [
          { key: "logIdList", type: "string[]", required: true, description: "조회할 로그 ID 목록" },
        ],
        response: [
          { key: "data", type: "object[]", description: "로그 상세 정보" },
          { key: "data[].logId", type: "string", description: "로그 ID" },
          { key: "data[].userId", type: "string", description: "사용자 ID" },
          { key: "data[].action", type: "string", description: "액션 유형" },
          { key: "data[].ip", type: "string", description: "접속 IP" },
          { key: "data[].createdDate", type: "string", description: "발생 일시" },
        ],
        responseType: "json",
      },
      {
        name: "getDocUserLog",
        method: "POST",
        version: "3.0",
        endpoint: "/wapi/logs/new/paging/contents/me",
        params: [
          { key: "page", type: "number", description: "페이지 번호" },
          { key: "pageSize", type: "number", description: "페이지당 항목 수" },
          { key: "startDate", type: "string", description: "조회 시작일 (YYYY-MM-DD)" },
          { key: "endDate", type: "string", description: "조회 종료일 (YYYY-MM-DD)" },
          { key: "workgroupCategoryList", type: "string[]", description: "워크그룹 카테고리 필터 (GENERAL, PRIVATE)" },
          { key: "filterDocument", type: "object", description: "문서 필터 조건" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "내 문서 로그 목록" },
          { key: "data.content[].logId", type: "string", description: "로그 ID" },
          { key: "data.content[].syncId", type: "string", description: "문서 ID" },
          { key: "data.content[].contentName", type: "string", description: "문서 이름" },
          { key: "data.content[].action", type: "string", description: "액션 유형" },
          { key: "data.content[].createdDate", type: "string", description: "발생 일시" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "getDocUserLogExcel",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/logs/new/paging/contents/me/excel",
        params: [
          { key: "startDate", type: "string", description: "조회 시작일 (YYYY-MM-DD)" },
          { key: "endDate", type: "string", description: "조회 종료일 (YYYY-MM-DD)" },
          { key: "workgroupCategoryList", type: "string[]", description: "워크그룹 카테고리 필터 (GENERAL, PRIVATE)" },
          { key: "filterDocument", type: "object", description: "문서 필터 조건" },
        ],
        responseType: "blob",
      },
    ],
  },
  {
    id: "meeting",
    label: "회의",
    section: "user",
    apis: [
      {
        name: "getMeetingList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/zoom/{workgroupId}/meetings/paging",
        params: [
          { key: "page", type: "number", description: "페이지 번호" },
          { key: "pageSize", type: "number", description: "페이지당 항목 수" },
          { key: "keyword", type: "string", description: "검색어" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "회의 목록" },
          { key: "data.content[].meetingId", type: "string", description: "회의 ID" },
          { key: "data.content[].title", type: "string", description: "회의 제목" },
          { key: "data.content[].startTime", type: "string", description: "회의 시작 시간" },
          { key: "data.content[].duration", type: "number", description: "회의 시간 (분)" },
          { key: "data.content[].status", type: "string", description: "회의 상태" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "getMeetingDetail",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/zoom/{workgroupId}/meetings/{meetingId}/detail",
        response: [
          { key: "data", type: "object", description: "회의 상세 정보" },
          { key: "data.meetingId", type: "string", description: "회의 ID" },
          { key: "data.title", type: "string", description: "회의 제목" },
          { key: "data.agenda", type: "string", description: "회의 안건" },
          { key: "data.startTime", type: "string", description: "회의 시작 시간 (ISO 8601)" },
          { key: "data.duration", type: "number", description: "회의 시간 (분)" },
          { key: "data.joinUrl", type: "string", description: "회의 참여 URL" },
          { key: "data.hostId", type: "string", description: "호스트 사용자 ID" },
        ],
        responseType: "json",
      },
      {
        name: "getMeetingDetailWithDiff",
        method: "GET",
        version: "~2.9",
        endpoint:
          "/wapi/zoom/{workgroupId}/meetings/{meetingId}/zoomdetailWithDiff",
        response: [
          { key: "data", type: "object", description: "변경 이력 포함 회의 상세 정보" },
          { key: "data.meetingId", type: "string", description: "회의 ID" },
          { key: "data.title", type: "string", description: "회의 제목" },
          { key: "data.startTime", type: "string", description: "회의 시작 시간" },
          { key: "data.diff", type: "object", description: "변경 이력 객체" },
        ],
        responseType: "json",
      },
      {
        name: "setMeetingFavorite",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/zoom/favorite",
        params: [
          {
            key: "meetingId",
            type: "string",
            required: true,
            description: "즐겨찾기 설정할 회의 ID",
          },
          {
            key: "flag",
            type: "boolean",
            required: true,
            description: "즐겨찾기 여부 (true: 추가, false: 해제)",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getMeetingListByKeyword",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/zoom/meetings/list",
        params: [
          {
            key: "keyword",
            type: "string",
            required: true,
            description: "검색어",
          },
          { key: "page", type: "number", description: "페이지 번호" },
          { key: "pageSize", type: "number", description: "페이지당 항목 수" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "키워드 검색 회의 목록" },
          { key: "data.content[].meetingId", type: "string", description: "회의 ID" },
          { key: "data.content[].title", type: "string", description: "회의 제목" },
          { key: "data.content[].startTime", type: "string", description: "회의 시작 시간" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "createMeeting",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/zoom/{workgroupId}/meetings",
        params: [
          {
            key: "title",
            type: "string",
            required: true,
            description: "회의 제목",
          },
          {
            key: "startTime",
            type: "string",
            required: true,
            description: "회의 시작 시간 (ISO 8601)",
          },
          { key: "duration", type: "number", description: "회의 시간 (분)" },
          { key: "agenda", type: "string", description: "회의 안건" },
        ],
        response: [
          { key: "data", type: "object", description: "생성된 회의 정보" },
          { key: "data.meetingId", type: "string", description: "생성된 회의 ID" },
          { key: "data.joinUrl", type: "string", description: "회의 참여 URL" },
          { key: "data.startUrl", type: "string", description: "호스트 시작 URL" },
        ],
        responseType: "json",
      },
      {
        name: "updateMeeting",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/zoom/{workgroupId}/meetings/{meetingId}/update",
        params: [
          { key: "title", type: "string", description: "회의 제목" },
          {
            key: "startTime",
            type: "string",
            description: "회의 시작 시간 (ISO 8601)",
          },
          { key: "duration", type: "number", description: "회의 시간 (분)" },
          { key: "agenda", type: "string", description: "회의 안건" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "deleteMeeting",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/zoom/{workgroupId}/meetings/{meetingId}/delete",
        response: [
        ],
        responseType: "json",
      },
    ],
  },
  {
    id: "chat",
    label: "채팅",
    section: "user",
    apis: [
      {
        name: "getChatUrl",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/chat/url/{workgroupId}",
        response: [
          { key: "data", type: "object", description: "채팅 URL 정보" },
          { key: "data.url", type: "string", description: "채팅 서비스 URL" },
        ],
        responseType: "json",
      },
      {
        name: "getRecentChat",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/chat/recent/{workgroupId}",
        response: [
          { key: "data", type: "object[]", description: "최근 채팅 메시지 목록" },
          { key: "data[].messageId", type: "string", description: "메시지 ID" },
          { key: "data[].content", type: "string", description: "메시지 내용" },
          { key: "data[].senderId", type: "string", description: "발신자 ID" },
          { key: "data[].createdDate", type: "string", description: "전송 일시" },
        ],
        responseType: "json",
      },
      {
        name: "getRecentChatList",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/chat/recent/list",
        response: [
          { key: "data", type: "object[]", description: "최근 채팅 워크그룹 목록" },
          { key: "data[].workgroupId", type: "string", description: "워크그룹 ID" },
          { key: "data[].lastMessage", type: "string", description: "마지막 메시지" },
          { key: "data[].unreadCount", type: "number", description: "미읽은 메시지 수" },
          { key: "data[].updatedDate", type: "string", description: "마지막 업데이트 일시" },
        ],
        responseType: "json",
      },
      {
        name: "getAccessToken",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/chat/accesToken",
        response: [
          { key: "data", type: "object", description: "채팅 액세스 토큰" },
          { key: "data.accessToken", type: "string", description: "채팅 서비스 액세스 토큰" },
          { key: "data.expiredTime", type: "number", description: "토큰 만료 시간 (ms)" },
        ],
        responseType: "json",
      },
    ],
  },

  // ──────────────── 관리자 섹션 ────────────────
  {
    id: "admin-home",
    label: "홈·대시보드",
    section: "admin",
    apis: [
      {
        name: "getDiskSpace",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/dashboard/diskspace",
        response: [
          { key: "data", type: "object", description: "디스크 사용량 정보" },
          { key: "data.total", type: "number", description: "전체 용량 (bytes)" },
          { key: "data.used", type: "number", description: "사용 용량 (bytes)" },
          { key: "data.free", type: "number", description: "남은 용량 (bytes)" },
        ],
        responseType: "json",
      },
      {
        name: "getCreatedWorkgroupCount",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/dashboard/workgroup",
        response: [
          { key: "data", type: "object", description: "워크그룹 수 통계" },
          { key: "data.total", type: "number", description: "전체 워크그룹 수" },
          { key: "data.active", type: "number", description: "활성 워크그룹 수" },
        ],
        responseType: "json",
      },
      {
        name: "getSharedDocCount",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/dashboard/share/doc",
        response: [
          { key: "data", type: "object", description: "공유 문서 수 통계" },
          { key: "data.total", type: "number", description: "전체 공유 문서 수" },
          { key: "data.today", type: "number", description: "오늘 공유된 문서 수" },
        ],
        responseType: "json",
      },
      {
        name: "getLicenseInfo",
        method: "GET",
        version: "3.0",
        endpoint: "/wapi/admins/dashboard/license",
        response: [
          { key: "data", type: "object", description: "라이선스 정보" },
          { key: "data.licenseType", type: "string", description: "라이선스 유형" },
          { key: "data.maxUsers", type: "number", description: "최대 사용자 수" },
          { key: "data.currentUsers", type: "number", description: "현재 사용자 수" },
          { key: "data.expiredDate", type: "string", description: "라이선스 만료일" },
        ],
        responseType: "json",
      },
      {
        name: "getMemberCnt",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/dashboard/member",
        response: [
          { key: "data", type: "object", description: "사용자 수 통계" },
          { key: "data.total", type: "number", description: "전체 사용자 수" },
          { key: "data.internal", type: "number", description: "내부 사용자 수" },
          { key: "data.external", type: "number", description: "외부 사용자 수" },
          { key: "data.active", type: "number", description: "활성 사용자 수" },
        ],
        responseType: "json",
      },
      {
        name: "getLineChart",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/dashboard/linechart",
        params: [
          {
            key: "startDate",
            type: "string",
            required: true,
            description: "조회 시작일 (YYYY-MM-DD)",
          },
          {
            key: "endDate",
            type: "string",
            required: true,
            description: "조회 종료일 (YYYY-MM-DD)",
          },
          { key: "type", type: "string", description: "차트 데이터 타입" },
        ],
        response: [
          { key: "data", type: "object", description: "라인 차트 데이터" },
          { key: "data.labels", type: "string[]", description: "X축 레이블 목록 (날짜)" },
          { key: "data.datasets", type: "object[]", description: "데이터셋 배열" },
          { key: "data.datasets[].label", type: "string", description: "데이터셋 이름" },
          { key: "data.datasets[].data", type: "number[]", description: "데이터 값 배열" },
        ],
        responseType: "json",
      },
      {
        name: "getTopMemberWorkgroup",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/dashboard/top/workgroup",
        response: [
          { key: "data", type: "object", description: "활동 상위 멤버/워크그룹" },
          { key: "data.topMembers", type: "object", description: "활동 상위 멤버 목록" },
          { key: "data.topWorkgroups", type: "object", description: "활동 상위 워크그룹 목록" },
        ],
        responseType: "json",
      },
      {
        name: "getTopDoc",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/dashboard/top/doc",
        response: [
          { key: "data", type: "object[]", description: "조회 상위 문서 목록" },
          { key: "data[].syncId", type: "string", description: "문서 고유 ID" },
          { key: "data[].contentName", type: "string", description: "문서 이름" },
          { key: "data[].viewCount", type: "number", description: "조회 수" },
        ],
        responseType: "json",
      },
      {
        name: "downloadChartExcel",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/dashboard/chart/excel",
        params: [
          {
            key: "startDate",
            type: "string",
            required: true,
            description: "조회 시작일 (YYYY-MM-DD)",
          },
          {
            key: "endDate",
            type: "string",
            required: true,
            description: "조회 종료일 (YYYY-MM-DD)",
          },
        ],
        responseType: "blob",
      },
    ],
  },
  {
    id: "admin-dept",
    label: "사용자·부서",
    section: "admin",
    apis: [
      {
        name: "getDeptLists",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/depts",
        response: [
          { key: "data", type: "object[]", description: "부서 목록" },
          { key: "data[].code", type: "string", description: "부서 코드" },
          { key: "data[].name", type: "string", description: "부서 이름" },
          { key: "data[].parentCode", type: "string", description: "상위 부서 코드" },
          { key: "data[].userCount", type: "number", description: "부서 소속 사용자 수" },
        ],
        responseType: "json",
      },
      {
        name: "getDeptDetail",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/depts/{deptId}/detail",
        response: [
          { key: "data", type: "object", description: "부서 상세 정보" },
          { key: "data.code", type: "string", description: "부서 코드" },
          { key: "data.name", type: "string", description: "부서 이름" },
          { key: "data.parentCode", type: "string", description: "상위 부서 코드" },
          { key: "data.userCount", type: "number", description: "부서 소속 사용자 수" },
        ],
        responseType: "json",
      },
      {
        name: "getDeptUsers",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/depts/{deptId}/users",
        response: [
          { key: "data", type: "object[]", description: "부서 소속 사용자 목록" },
          { key: "data[].userId", type: "string", description: "사용자 ID" },
          { key: "data[].userName", type: "string", description: "사용자 이름" },
          { key: "data[].email", type: "string", description: "이메일" },
          { key: "data[].positionName", type: "string", description: "직위 이름" },
        ],
        responseType: "json",
      },
      {
        name: "getDeptInfo",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/depts/{deptId}",
        response: [
          { key: "data", type: "object", description: "부서 기본 정보" },
          { key: "data.code", type: "string", description: "부서 코드" },
          { key: "data.name", type: "string", description: "부서 이름" },
          { key: "data.parentCode", type: "string", description: "상위 부서 코드" },
        ],
        responseType: "json",
      },
      {
        name: "getUserDetail",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/users/{userId}",
        response: [
          { key: "data", type: "object", description: "사용자 상세 정보" },
          { key: "data.userId", type: "string", description: "사용자 ID" },
          { key: "data.userName", type: "string", description: "사용자 이름" },
          { key: "data.email", type: "string", description: "이메일" },
          { key: "data.deptCode", type: "string", description: "부서 코드" },
          { key: "data.positionCode", type: "string", description: "직위 코드" },
          { key: "data.status", type: "number", description: "계정 상태 (1: 활성, 0: 비활성)" },
          { key: "data.userType", type: "string", description: "사용자 유형 (internal / external)" },
          { key: "data.createdDate", type: "string", description: "생성일시" },
        ],
        responseType: "json",
      },
      {
        name: "updateUser",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/users/update",
        params: [
          {
            key: "userId",
            type: "string",
            required: true,
            description: "수정할 사용자 ID",
          },
          { key: "name", type: "string", description: "사용자 이름" },
          { key: "email", type: "string", description: "이메일 주소" },
          { key: "deptCode", type: "string", description: "부서 코드" },
          { key: "positionCode", type: "string", description: "직위 코드" },
          { key: "status", type: "number", description: "계정 상태" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "updateExternalUser",
        method: "POST",
        version: "3.0",
        endpoint: "/wapi/admins/users/update/external",
        params: [
          {
            key: "userId",
            type: "string",
            required: true,
            description: "수정할 외부 사용자 ID",
          },
          { key: "name", type: "string", description: "사용자 이름" },
          { key: "status", type: "number", description: "계정 상태" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "insertUser",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/users/insert",
        params: [
          {
            key: "userId",
            type: "string",
            required: true,
            description: "사용자 ID",
          },
          {
            key: "name",
            type: "string",
            required: true,
            description: "사용자 이름",
          },
          {
            key: "email",
            type: "string",
            required: true,
            description: "이메일 주소",
          },
          { key: "deptCode", type: "string", description: "부서 코드" },
          { key: "positionCode", type: "string", description: "직위 코드" },
          { key: "password", type: "string", description: "초기 비밀번호" },
        ],
        response: [
          { key: "data", type: "object", description: "생성된 사용자 정보" },
          { key: "data.userId", type: "string", description: "사용자 ID" },
          { key: "data.userName", type: "string", description: "사용자 이름" },
        ],
        responseType: "json",
      },
      {
        name: "getActiveInternalUserList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/users/list/internal/active",
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "활성 내부 사용자 목록" },
          { key: "data.content[].userId", type: "string", description: "사용자 ID" },
          { key: "data.content[].userName", type: "string", description: "사용자 이름" },
          { key: "data.content[].email", type: "string", description: "이메일" },
          { key: "data.content[].deptName", type: "string", description: "부서 이름" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "getActiveExternalUserList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/users/list/external/active",
        params: [
          { key: "userType", type: "number", description: "사용자 유형 (고정값: 2)" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "활성 외부 사용자 목록" },
          { key: "data.content[].userId", type: "string", description: "사용자 ID" },
          { key: "data.content[].userName", type: "string", description: "사용자 이름" },
          { key: "data.content[].email", type: "string", description: "이메일" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "getInActiveExternalUserList",
        method: "POST",
        version: "3.0",
        endpoint: "/wapi/admins/users/list/external/inactive",
        params: [
          { key: "searchOpt", type: "string", description: "검색어" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "비활성 외부 사용자 목록" },
          { key: "data.content[].userId", type: "string", description: "사용자 ID" },
          { key: "data.content[].userName", type: "string", description: "사용자 이름" },
          { key: "data.content[].email", type: "string", description: "이메일" },
          { key: "data.content[].invitedDate", type: "string", description: "초대일시" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "getExternalLiteUserList",
        method: "POST",
        version: "3.0",
        endpoint: "/wapi/admins/users/list/external/lite",
        params: [
          { key: "searchOpt", type: "string", description: "검색어" },
          { key: "userType", type: "number", description: "사용자 유형 (고정값: 3)" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "외부 라이트 사용자 목록" },
          { key: "data.content[].userId", type: "string", description: "사용자 ID" },
          { key: "data.content[].userName", type: "string", description: "사용자 이름" },
          { key: "data.content[].email", type: "string", description: "이메일" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "getInternalUserList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/users/list",
        params: [
          { key: "searchOpt", type: "string", description: "검색어" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "내부 사용자 목록" },
          { key: "data.content[].userId", type: "string", description: "사용자 ID" },
          { key: "data.content[].userName", type: "string", description: "사용자 이름" },
          { key: "data.content[].email", type: "string", description: "이메일" },
          { key: "data.content[].deptName", type: "string", description: "부서 이름" },
          { key: "data.content[].status", type: "number", description: "계정 상태" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "getAllUserList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/users/list/all",
        params: [
          { key: "searchOpt", type: "string", description: "검색어" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "전체 사용자 목록" },
          { key: "data.content[].userId", type: "string", description: "사용자 ID" },
          { key: "data.content[].userName", type: "string", description: "사용자 이름" },
          { key: "data.content[].email", type: "string", description: "이메일" },
          { key: "data.content[].userType", type: "string", description: "사용자 유형" },
          { key: "data.content[].status", type: "number", description: "계정 상태" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "getExternalUserList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/users/list/external",
        params: [
          { key: "searchOpt", type: "string", description: "검색어" },
          { key: "userTypes", type: "number[]", description: "사용자 유형 목록 (기본값: [2, 3])" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "외부 사용자 목록" },
          { key: "data.content[].userId", type: "string", description: "사용자 ID" },
          { key: "data.content[].userName", type: "string", description: "사용자 이름" },
          { key: "data.content[].email", type: "string", description: "이메일" },
          { key: "data.content[].status", type: "number", description: "계정 상태" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "downloadUserExcel",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/users/list/excel",
        responseType: "blob",
      },
      {
        name: "uploadUserExcel",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/users/list/excel/upload",
        response: [
          { key: "data", type: "object", description: "엑셀 업로드 결과" },
          { key: "data.totalCount", type: "number", description: "전체 처리 건수" },
          { key: "data.successCount", type: "number", description: "성공 건수" },
          { key: "data.failCount", type: "number", description: "실패 건수" },
          { key: "data.errors", type: "object", description: "실패 항목 목록" },
        ],
        responseType: "json",
      },
      {
        name: "updateDept",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/depts/update",
        params: [
          {
            key: "code",
            type: "string",
            required: true,
            description: "수정할 부서 코드",
          },
          {
            key: "name",
            type: "string",
            required: true,
            description: "변경할 부서 이름",
          },
          { key: "parentCode", type: "string", description: "상위 부서 코드" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "insertDept",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/depts/insert",
        params: [
          {
            key: "name",
            type: "string",
            required: true,
            description: "부서 이름",
          },
          {
            key: "parentCode",
            type: "string",
            description: "상위 부서 코드 (최상위 시 생략)",
          },
          {
            key: "code",
            type: "string",
            description: "부서 코드 (생략 시 자동 생성)",
          },
        ],
        response: [
          { key: "data", type: "object", description: "생성된 부서 정보" },
          { key: "data.code", type: "string", description: "생성된 부서 코드" },
          { key: "data.name", type: "string", description: "부서 이름" },
        ],
        responseType: "json",
      },
      {
        name: "deleteDept",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/depts/delete/{code}",
        response: [
        ],
        responseType: "json",
      },
      {
        name: "deleteUser",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/users/delete",
        params: [
          {
            key: "userIds",
            type: "string[]",
            required: true,
            description: "삭제할 사용자 ID 목록",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "updateUserPassword",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/users/passwd/update",
        params: [
          {
            key: "userId",
            type: "string",
            required: true,
            description: "비밀번호를 변경할 사용자 ID",
          },
          {
            key: "password",
            type: "string",
            required: true,
            description: "새 비밀번호",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "initAdminPassword",
        method: "GET",
        version: "~2.9",
        endpoint: "/papi/passwd/init/admin/{param}",
        response: [
        ],
        responseType: "json",
      },
    ],
  },
  {
    id: "admin-workgroup",
    label: "워크그룹",
    section: "admin",
    apis: [
      {
        name: "getWorkgroupList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/workgroups/paging",
        params: [
          { key: "page", type: "number", description: "페이지 번호" },
          { key: "pageSize", type: "number", description: "페이지당 항목 수" },
          { key: "keyword", type: "string", description: "검색어" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "워크그룹 목록" },
          { key: "data.content[].workgroupId", type: "string", description: "워크그룹 ID" },
          { key: "data.content[].name", type: "string", description: "워크그룹 이름" },
          { key: "data.content[].memberCount", type: "number", description: "멤버 수" },
          { key: "data.content[].docCount", type: "number", description: "문서 수" },
          { key: "data.content[].status", type: "number", description: "상태" },
          { key: "data.content[].createdDate", type: "string", description: "생성일시" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "updateWorkgroupStatus",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/workgroups/status",
        params: [
          {
            key: "workgroupId",
            type: "string",
            required: true,
            description: "워크그룹 ID",
          },
          {
            key: "status",
            type: "number",
            required: true,
            description: "변경할 상태값",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getWorkgroupDetail",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/workgroups",
        params: [
          {
            key: "workgroupId",
            type: "string",
            required: true,
            description: "워크그룹 ID",
          },
        ],
        response: [
          { key: "data", type: "object", description: "워크그룹 상세 정보" },
          { key: "data.workgroupId", type: "string", description: "워크그룹 ID" },
          { key: "data.name", type: "string", description: "워크그룹 이름" },
          { key: "data.description", type: "string", description: "설명" },
          { key: "data.type", type: "string", description: "워크그룹 유형" },
          { key: "data.status", type: "number", description: "상태" },
          { key: "data.memberCount", type: "number", description: "멤버 수" },
          { key: "data.createdDate", type: "string", description: "생성일시" },
        ],
        responseType: "json",
      },
      {
        name: "getContentList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/contents/list",
        params: [
          {
            key: "workgroupId",
            type: "string",
            required: true,
            description: "워크그룹 ID",
          },
          { key: "page", type: "number", description: "페이지 번호" },
          { key: "pageSize", type: "number", description: "페이지당 항목 수" },
          { key: "keyword", type: "string", description: "검색어" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "콘텐츠 목록" },
          { key: "data.content[].syncId", type: "string", description: "문서 고유 ID" },
          { key: "data.content[].contentName", type: "string", description: "문서 이름" },
          { key: "data.content[].fileSize", type: "number", description: "파일 크기 (bytes)" },
          { key: "data.content[].createdDate", type: "string", description: "생성일시" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "downloadContent",
        method: "GET",
        version: "~2.9",
        endpoint:
          "/wapi/admins/contents/download/{syncId}/revisions/{revisionNo}",
        responseType: "blob",
      },
      {
        name: "updateWorkgroupManager",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/workgroups/manager",
        params: [
          {
            key: "workgroupId",
            type: "string",
            required: true,
            description: "워크그룹 ID",
          },
          {
            key: "userId",
            type: "string",
            required: true,
            description: "새 관리자로 지정할 사용자 ID",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
    ],
  },
  {
    id: "admin-content",
    label: "콘텐츠",
    section: "admin",
    apis: [
      {
        name: "getContentList",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/contents/list",
        params: [
          {
            key: "workgroupId",
            type: "string",
            required: true,
            description: "워크그룹 ID",
          },
          { key: "page", type: "number", description: "페이지 번호" },
          { key: "pageSize", type: "number", description: "페이지당 항목 수" },
          { key: "keyword", type: "string", description: "검색어" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "콘텐츠 목록" },
          { key: "data.content[].syncId", type: "string", description: "문서 고유 ID" },
          { key: "data.content[].contentName", type: "string", description: "문서 이름" },
          { key: "data.content[].fileSize", type: "number", description: "파일 크기 (bytes)" },
          { key: "data.content[].createdDate", type: "string", description: "생성일시" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "deleteDocNow",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/contents/delete/now",
        params: [{ key: "syncIds", type: "string[]", required: true }],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "restoreDoc",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/contents/restore",
        params: [{ key: "syncIds", type: "string[]", required: true }],
        response: [
        ],
        responseType: "json",
      },
    ],
  },
  {
    id: "admin-setting",
    label: "설정",
    section: "admin",
    apis: [
      {
        name: "getManageType",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/manage/type",
        response: [
          { key: "data", type: "object[]", description: "관리 유형 목록" },
          { key: "data[].id", type: "string", description: "관리 유형 ID" },
          { key: "data[].name", type: "string", description: "관리 유형 이름" },
          { key: "data[].description", type: "string", description: "설명" },
        ],
        responseType: "json",
      },
      {
        name: "createManageType",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/manage/type/create",
        params: [
          {
            key: "name",
            type: "string",
            required: true,
            description: "관리 유형 이름",
          },
          { key: "description", type: "string", description: "관리 유형 설명" },
        ],
        response: [
          { key: "data", type: "object", description: "생성된 관리 유형 정보" },
          { key: "data.id", type: "string", description: "관리 유형 ID" },
          { key: "data.name", type: "string", description: "관리 유형 이름" },
        ],
        responseType: "json",
      },
      {
        name: "updateManageType",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/manage/type/update",
        params: [
          {
            key: "id",
            type: "string",
            required: true,
            description: "수정할 관리 유형 ID",
          },
          {
            key: "name",
            type: "string",
            required: true,
            description: "변경할 이름",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "deleteManageType",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/manage/type/delete",
        params: [
          { key: "codes", type: "string[]", required: true, description: "삭제할 관리 유형 코드 목록" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getAssignment",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/manage/assignment",
        response: [
          { key: "data", type: "object[]", description: "배정 목록" },
          { key: "data[].id", type: "string", description: "배정 ID" },
          { key: "data[].name", type: "string", description: "배정 이름" },
          { key: "data[].typeId", type: "string", description: "관리 유형 ID" },
        ],
        responseType: "json",
      },
      {
        name: "createAssignment",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/manage/assignment/create",
        params: [
          {
            key: "name",
            type: "string",
            required: true,
            description: "배정 이름",
          },
          {
            key: "typeId",
            type: "string",
            required: true,
            description: "관리 유형 ID",
          },
        ],
        response: [
          { key: "data", type: "object", description: "생성된 배정 정보" },
          { key: "data.id", type: "string", description: "배정 ID" },
          { key: "data.name", type: "string", description: "배정 이름" },
        ],
        responseType: "json",
      },
      {
        name: "updateAssignment",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/manage/assignment/update",
        params: [
          {
            key: "id",
            type: "string",
            required: true,
            description: "수정할 배정 ID",
          },
          { key: "name", type: "string", description: "변경할 이름" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "deleteAssignment",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/manage/assignment/delete",
        params: [
          { key: "deleteIds", type: "string[]", required: true, description: "삭제할 배정 ID 목록" },
        ],
        response: [
        ],
        responseType: "json",
      },
    ],
  },
  {
    id: "admin-position",
    label: "직위·역할",
    section: "admin",
    apis: [
      {
        name: "getPositionList",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/positions",
        response: [
          { key: "data", type: "object[]", description: "직위 목록" },
          { key: "data[].code", type: "string", description: "직위 코드" },
          { key: "data[].name", type: "string", description: "직위 이름" },
        ],
        responseType: "json",
      },
      {
        name: "insertPosition",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/positions/insert",
        params: [{ key: "name", type: "string", required: true }],
        response: [
          { key: "data", type: "object", description: "생성된 직위 정보" },
          { key: "data.code", type: "string", description: "직위 코드" },
          { key: "data.name", type: "string", description: "직위 이름" },
        ],
        responseType: "json",
      },
      {
        name: "updatePosition",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/positions/update",
        params: [
          { key: "code", type: "string", required: true },
          { key: "name", type: "string", required: true },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "deletePosition",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/positions/delete",
        params: [{ key: "codes", type: "object[]", required: true, description: "삭제할 직위 코드 객체 목록 ({ code: string }[])" }],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getRoleList",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/roles",
        response: [
          { key: "data", type: "object[]", description: "역할 목록" },
          { key: "data[].code", type: "string", description: "역할 코드" },
          { key: "data[].name", type: "string", description: "역할 이름" },
        ],
        responseType: "json",
      },
      {
        name: "insertRole",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/roles/insert",
        params: [{ key: "name", type: "string", required: true }],
        response: [
          { key: "data", type: "object", description: "생성된 역할 정보" },
          { key: "data.code", type: "string", description: "역할 코드" },
          { key: "data.name", type: "string", description: "역할 이름" },
        ],
        responseType: "json",
      },
      {
        name: "updateRole",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/roles/update",
        params: [
          { key: "code", type: "string", required: true },
          { key: "name", type: "string", required: true },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "deleteRole",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/roles/delete",
        params: [{ key: "codes", type: "object[]", required: true, description: "삭제할 역할 코드 객체 목록 ({ code: string }[])" }],
        response: [
        ],
        responseType: "json",
      },
    ],
  },
  {
    id: "admin-whitelabel",
    label: "화이트라벨",
    section: "admin",
    apis: [
      {
        name: "getWhiteLabel",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/config/whitelabel",
        response: [
          { key: "data", type: "object", description: "화이트라벨 설정" },
          { key: "data.logoUrl", type: "string", description: "로고 이미지 URL" },
          { key: "data.primaryColor", type: "string", description: "주 색상 (HEX 코드)" },
          { key: "data.companyName", type: "string", description: "회사명" },
          { key: "data.faviconUrl", type: "string", description: "파비콘 URL" },
        ],
        responseType: "json",
      },
      {
        name: "updateWhiteLabel",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/config/whitelabel",
        params: [
          { key: "logoUrl", type: "string", description: "로고 이미지 URL" },
          {
            key: "primaryColor",
            type: "string",
            description: "주 색상 (HEX 코드)",
          },
          { key: "companyName", type: "string", description: "회사명" },
          { key: "faviconUrl", type: "string", description: "파비콘 URL" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "resetWhiteLabel",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/config/whitelabel/reset",
        response: [
        ],
        responseType: "json",
      },
    ],
  },
  {
    id: "admin-log",
    label: "로그",
    section: "admin",
    apis: [
      {
        name: "getLoginLog",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/logs/login",
        params: [
          { key: "page", type: "number", description: "페이지 번호" },
          { key: "pageSize", type: "number", description: "페이지당 항목 수" },
          {
            key: "startDate",
            type: "string",
            description: "조회 시작일 (YYYY-MM-DD)",
          },
          {
            key: "endDate",
            type: "string",
            description: "조회 종료일 (YYYY-MM-DD)",
          },
          {
            key: "keyword",
            type: "string",
            description: "검색어 (사용자 ID, 이메일 등)",
          },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "로그인 로그 목록" },
          { key: "data.content[].logId", type: "string", description: "로그 ID" },
          { key: "data.content[].userId", type: "string", description: "사용자 ID" },
          { key: "data.content[].ip", type: "string", description: "접속 IP" },
          { key: "data.content[].result", type: "string", description: "로그인 결과 (success / fail)" },
          { key: "data.content[].createdDate", type: "string", description: "발생 일시" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "getLoginLogExcel",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/logs/login/excel",
        params: [
          {
            key: "startDate",
            type: "string",
            description: "조회 시작일 (YYYY-MM-DD)",
          },
          {
            key: "endDate",
            type: "string",
            description: "조회 종료일 (YYYY-MM-DD)",
          },
          { key: "keyword", type: "string", description: "검색어" },
        ],
        responseType: "blob",
      },
      {
        name: "getWorkgroupLog",
        method: "POST",
        version: "3.0",
        endpoint: "/wapi/admins/logs/new/paging",
        params: [
          { key: "page", type: "number", description: "페이지 번호" },
          { key: "pageSize", type: "number", description: "페이지당 항목 수" },
          { key: "workgroupId", type: "string", description: "워크그룹 ID 필터" },
          { key: "startDate", type: "string", description: "조회 시작일 (YYYY-MM-DD)" },
          { key: "endDate", type: "string", description: "조회 종료일 (YYYY-MM-DD)" },
          { key: "workgroupCategoryList", type: "string[]", description: "워크그룹 카테고리 필터" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "워크그룹 활동 로그 목록" },
          { key: "data.content[].logId", type: "string", description: "로그 ID" },
          { key: "data.content[].userId", type: "string", description: "사용자 ID" },
          { key: "data.content[].workgroupId", type: "string", description: "워크그룹 ID" },
          { key: "data.content[].action", type: "string", description: "액션 유형" },
          { key: "data.content[].createdDate", type: "string", description: "발생 일시" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "getWorkgroupLogExcel",
        method: "POST",
        version: "3.0",
        endpoint: "/wapi/admins/logs/new/paging/excel",
        params: [
          { key: "workgroupId", type: "string", description: "워크그룹 ID 필터" },
          { key: "startDate", type: "string", description: "조회 시작일 (YYYY-MM-DD)" },
          { key: "endDate", type: "string", description: "조회 종료일 (YYYY-MM-DD)" },
          { key: "workgroupCategoryList", type: "string[]", description: "워크그룹 카테고리 필터" },
          { key: "timezoneOffset", type: "number", description: "타임존 오프셋" },
        ],
        responseType: "blob",
      },
      {
        name: "getDocLog",
        method: "POST",
        version: "3.0",
        endpoint: "/wapi/admins/logs/new/paging",
        params: [
          { key: "page", type: "number", description: "페이지 번호" },
          { key: "pageSize", type: "number", description: "페이지당 항목 수" },
          { key: "syncId", type: "string", description: "문서 ID 필터" },
          { key: "startDate", type: "string", description: "조회 시작일 (YYYY-MM-DD)" },
          { key: "endDate", type: "string", description: "조회 종료일 (YYYY-MM-DD)" },
          { key: "workgroupCategoryList", type: "string[]", description: "워크그룹 카테고리 필터" },
          { key: "filterDocument", type: "object", description: "문서 필터 조건" },
        ],
        response: [
          { key: "data", type: "object", description: "페이지 응답 객체" },
          { key: "data.content", type: "object[]", description: "문서 활동 로그 목록" },
          { key: "data.content[].logId", type: "string", description: "로그 ID" },
          { key: "data.content[].userId", type: "string", description: "사용자 ID" },
          { key: "data.content[].syncId", type: "string", description: "문서 ID" },
          { key: "data.content[].action", type: "string", description: "액션 유형" },
          { key: "data.content[].createdDate", type: "string", description: "발생 일시" },
          { key: "data.totalElements", type: "number", description: "전체 항목 수" },
          { key: "data.totalPages", type: "number", description: "전체 페이지 수" },
          { key: "data.page", type: "number", description: "현재 페이지 번호" },
        ],
        responseType: "json",
      },
      {
        name: "getDocLogExcel",
        method: "POST",
        version: "3.0",
        endpoint: "/wapi/admins/logs/new/paging/excel",
        params: [
          { key: "syncId", type: "string", description: "문서 ID 필터" },
          { key: "startDate", type: "string", description: "조회 시작일 (YYYY-MM-DD)" },
          { key: "endDate", type: "string", description: "조회 종료일 (YYYY-MM-DD)" },
          { key: "workgroupCategoryList", type: "string[]", description: "워크그룹 카테고리 필터" },
          { key: "filterDocument", type: "object", description: "문서 필터 조건" },
          { key: "timezoneOffset", type: "number", description: "타임존 오프셋" },
        ],
        responseType: "blob",
      },
    ],
  },
  {
    id: "admin-login",
    label: "로그인",
    section: "admin",
    apis: [
      {
        name: "getAdminSession",
        method: "POST",
        version: "~2.9",
        endpoint: "/sessions/admins/new",
        response: [
          { key: "data", type: "object", description: "어드민 세션 정보" },
          { key: "data.sessionId", type: "string", description: "세션 ID" },
          { key: "data.csrfToken", type: "string", description: "CSRF 토큰" },
        ],
        responseType: "json",
      },
      {
        name: "adminLogin",
        method: "POST",
        version: "~2.9",
        endpoint: "/sessions/admins/login",
        params: [
          {
            key: "userId",
            type: "string",
            required: true,
            description: "관리자 ID",
          },
          {
            key: "password",
            type: "string",
            required: true,
            description: "비밀번호",
          },
        ],
        response: [
          { key: "data", type: "object", description: "인증 정보 객체" },
          { key: "data.token", type: "string", description: "API 인증 토큰" },
          { key: "data.userId", type: "string", description: "사용자 ID" },
          { key: "data.userName", type: "string", description: "사용자 이름" },
          { key: "data.dsdCode", type: "string", description: "워크스페이스 코드" },
          { key: "data.expiredTime", type: "number", description: "토큰 만료 시간 (ms)" },
          { key: "data.adminLevel", type: "number", description: "관리자 권한 레벨" },
        ],
        responseType: "json",
      },
      {
        name: "adminOtpNew",
        method: "POST",
        version: "~2.9",
        endpoint: "/sessions/admins/otp/new",
        params: [
          {
            key: "userId",
            type: "string",
            required: true,
            description: "관리자 ID",
          },
        ],
        response: [
          { key: "data.otpType", type: "string", description: "OTP 방식" },
        ],
        responseType: "json",
      },
      {
        name: "adminOtpLogin",
        method: "POST",
        version: "~2.9",
        endpoint: "/sessions/admins/otp/login",
        params: [
          {
            key: "otp",
            type: "string",
            required: true,
            description: "OTP 코드",
          },
        ],
        response: [
          { key: "data", type: "object", description: "인증 정보 객체" },
          { key: "data.token", type: "string", description: "API 인증 토큰" },
          { key: "data.userId", type: "string", description: "사용자 ID" },
          { key: "data.userName", type: "string", description: "사용자 이름" },
          { key: "data.dsdCode", type: "string", description: "워크스페이스 코드" },
          { key: "data.expiredTime", type: "number", description: "토큰 만료 시간 (ms)" },
        ],
        responseType: "json",
      },
      {
        name: "updateAdminPassword",
        method: "POST",
        version: "~2.9",
        endpoint: "/papi/passwd/admin/update",
        params: [
          { key: "userId", type: "string", required: true, description: "관리자 ID" },
          { key: "password", type: "string", required: true, description: "새 비밀번호" },
          { key: "initOtp", type: "boolean", description: "OTP 초기화 여부 (query string)" },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "adminSupport",
        method: "POST",
        version: "3.0",
        endpoint: "/wapi/admins/support",
        params: [{ key: "message", type: "string", required: true }],
        response: [
        ],
        responseType: "json",
      },
    ],
  },
  {
    id: "admin-config",
    label: "환경설정",
    section: "admin",
    apis: [
      {
        name: "getConfig",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/config",
        response: [
          { key: "data", type: "object[]", description: "시스템 설정 목록" },
          { key: "data[].key", type: "string", description: "설정 키" },
          { key: "data[].value", type: "string", description: "설정 값" },
          { key: "data[].description", type: "string", description: "설정 설명" },
        ],
        responseType: "json",
      },
      {
        name: "updateConfig",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/config",
        params: [
          {
            key: "key",
            type: "string",
            required: true,
            description: "설정 키",
          },
          {
            key: "value",
            type: "string",
            required: true,
            description: "설정 값",
          },
        ],
        response: [
        ],
        responseType: "json",
      },
      {
        name: "getFwocConfig",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/config/fwoc",
        response: [
          { key: "data", type: "object", description: "FWOC 설정 정보" },
          { key: "data.enabled", type: "boolean", description: "FWOC 활성화 여부" },
          { key: "data.serverUrl", type: "string", description: "FWOC 서버 URL" },
        ],
        responseType: "json",
      },
      {
        name: "getIpConfig",
        method: "GET",
        version: "~2.9",
        endpoint: "/wapi/admins/config/access/control",
        response: [
          { key: "data", type: "object", description: "IP 접근 제어 설정" },
          { key: "data.enabled", type: "boolean", description: "IP 접근 제어 활성화 여부" },
          { key: "data.ipList", type: "string[]", description: "허용 IP 목록" },
        ],
        responseType: "json",
      },
      {
        name: "updateIpConfig",
        method: "POST",
        version: "~2.9",
        endpoint: "/wapi/admins/config/access/control",
        params: [
          {
            key: "enabled",
            type: "boolean",
            required: true,
            description: "IP 접근 제어 활성화 여부",
          },
          { key: "ipList", type: "string[]", description: "허용할 IP 목록" },
        ],
        response: [
        ],
        responseType: "json",
      },
    ],
  },
];
